/* cz-addons.js — Incisione + Pacchetto Celebra l'Amore
   Custom elements:
     <cz-product-addons>          — card add-on PDP (trigger drawer incisione + drawer gift)
     <cz-engraving-drawer>        — drawer Incisione (registrato in Task 10)
     <cz-gift-package-drawer>     — drawer Celebra l'Amore (registrato in Task 13)
     <cz-cart-package-manager>    — riquadro + drawer cart (registrato in Task 21+)

   Singletons:
     CzAddonSync                  — intercept add-to-cart (registrato in Task 9)
     CzPackageOps                 — operazioni cart move/split/merge/remove (Task 23+)
*/

(function () {
  'use strict';

  // Il file è caricato da theme.liquid E dalle sezioni cart/product-drawer:
  // sulla stessa pagina può eseguire due volte. La seconda esecuzione deve
  // uscire subito, o la prima customElements.define esplode.
  if (customElements.get('cz-product-addons')) return;

  // ============================================================
  // <cz-product-addons>
  // ============================================================
  class CzProductAddons extends HTMLElement {
    constructor() {
      super();
      this.state = {
        engraving: { active: false, text: '', font: 'script', metalColor: 'argento' },
        gift: { active: false, photoUrl: null, photoPublicId: null, dedication: '' }
      };
    }

    connectedCallback() {
      this.engravingMax = parseInt(this.dataset.engravingMax, 10) || 40;
      this.dedicationMax = parseInt(this.dataset.dedicationMax, 10) || 30;
      this._wireCards();
      // Testi originali dei riassunti (da Liquid): servono per il reset.
      if (!this._testiIniziali) {
        this._testiIniziali = {
          engraving: this.querySelector('[data-engraving-summary]')?.textContent ?? null,
          gift: this.querySelector('[data-gift-summary]')?.textContent ?? null,
        };
      }
      // Quando il prodotto esce dal carrello, la checklist torna pulita.
      // Solo se gli addon erano stati AGGIUNTI (this._aggiunto): una
      // configurazione in corso non ancora aggiunta non si tocca.
      if (!this._sorvegliaCarrello) {
        this._sorvegliaCarrello = async () => {
          if (!this._aggiunto) return;
          try {
            const cart = await (await fetch('/cart.js')).json();
            const presente = cart.items.some((it) => it.handle === this.dataset.productHandle);
            if (!presente) this._resetAddons();
          } catch (err) { /* silenzioso: solo cosmesi PDP */ }
        };
        document.addEventListener('cz:cart:updated', this._sorvegliaCarrello);
        document.addEventListener('cart:updated', this._sorvegliaCarrello);
      }
    }

    _resetAddons() {
      this._aggiunto = false;
      this.state.engraving = { active: false, text: '', font: 'script', metalColor: 'argento' };
      this.state.gift = { active: false, photoUrl: null, photoPublicId: null, dedication: '' };
      this.querySelectorAll('.cz-addon-card').forEach((card) => {
        card.classList.remove('is-active');
        card.setAttribute('aria-pressed', 'false');
      });
      const se = this.querySelector('[data-engraving-summary]');
      if (se && this._testiIniziali?.engraving !== null) se.textContent = this._testiIniziali.engraving;
      const sg = this.querySelector('[data-gift-summary]');
      if (sg && this._testiIniziali?.gift !== null) sg.textContent = this._testiIniziali.gift;
    }

    _wireCards() {
      this.querySelectorAll('.cz-addon-card').forEach((card) => {
        if (card.dataset.czWired === '1') return;
        card.dataset.czWired = '1';
        card.addEventListener('click', (e) => {
          if (card.dataset.action === 'open-gift-drawer') {
            this._openGiftDrawer();
            return;
          }
          if (card.dataset.action === 'open-engraving-drawer') {
            this._openEngravingDrawer();
            return;
          }
          this._toggleCard(card);
        });
      });
    }

    _toggleCard(card) {
      const addon = card.dataset.addon; // 'engraving' or 'gift'
      const isActive = card.classList.toggle('is-active');
      card.setAttribute('aria-pressed', String(isActive));
      this.state[addon].active = isActive;
    }

    _openGiftDrawer() {
      // Dispatch event globale — handled da CzGiftPackageDrawer
      const detail = {
        source: 'pdp',
        productHandle: this.dataset.productHandle,
        productTitle: this.dataset.productTitle,
        currentConfig: this.state.gift
      };
      document.dispatchEvent(new CustomEvent('cz:gift-drawer:open', { detail }));
    }

    _openEngravingDrawer() {
      const detail = {
        source: 'pdp',
        productHandle: this.dataset.productHandle,
        productTitle: this.dataset.productTitle,
        metalColor: this.dataset.metalColor || 'argento',
        currentConfig: this.state.engraving
      };
      document.dispatchEvent(new CustomEvent('cz:engraving-drawer:open', { detail }));
    }

    // Ricevuto dal drawer al "Conferma"
    setGiftConfig({ photoUrl, photoPublicId, dedication }) {
      this.state.gift.photoUrl = photoUrl;
      this.state.gift.photoPublicId = photoPublicId;
      this.state.gift.dedication = dedication;
      this.state.gift.active = true;

      const card = this.querySelector('[data-addon="gift"]');
      if (card) {
        card.classList.add('is-active');
        card.setAttribute('aria-pressed', 'true');
        const summary = card.querySelector('[data-gift-summary]');
        if (summary) {
          summary.textContent = photoUrl
            ? 'Foto + dedica salvate ✓'
            : 'Dedica salvata ✓';
        }
      }
    }

    setEngravingConfig({ text, font, metalColor }) {
      this.state.engraving.text = text;
      this.state.engraving.font = font;
      this.state.engraving.metalColor = metalColor;
      this.state.engraving.active = true;

      const card = this.querySelector('[data-addon="engraving"]');
      if (card) {
        card.classList.add('is-active');
        card.setAttribute('aria-pressed', 'true');
        const summary = card.querySelector('[data-engraving-summary]');
        if (summary) {
          const fontLabel = { script: 'Script', serif: 'Serif', sans: 'Block' }[font] || 'Script';
          summary.textContent = `${fontLabel} · "${text}"`;
        }
      }
    }
  }

  customElements.define('cz-product-addons', CzProductAddons);

  // ============================================================
  // CzAddonSync — intercept add-to-cart, costruisce items[] atomico
  // ============================================================
  const CzAddonSync = {
    init() {
      // Wrappa il submit di OGNI form add-to-cart dentro un <cz-product-addons>.
      document.addEventListener('submit', (e) => {
        const form = e.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (form.getAttribute('action') !== '/cart/add') return;

        const aside = form.closest('.product-drawer__main, .product-drawer__aside, aside');
        if (!aside) return;
        const addons = aside.querySelector('cz-product-addons');
        if (!addons) return;

        const state = addons.state;
        const hasEngraving = state.engraving.active && state.engraving.text.trim().length > 0;
        const hasGift = state.gift.active && state.gift.dedication.trim().length > 0;
        if (!hasEngraving && !hasGift) return; // nessun add-on, lascia il submit normale

        e.preventDefault();
        e.stopImmediatePropagation();
        this._submitWithAddons(form, addons, state, { hasEngraving, hasGift });
      }, true); // capture phase per arrivare prima del theme default
    },

    async _submitWithAddons(form, addons, state, flags) {
      try {
      const variantId = form.querySelector('input[name="id"]').value;
      const productTitle = addons.dataset.productTitle;
      const productHandle = addons.dataset.productHandle;

      const items = [];

      // Parent product line item
      const parentProps = {};
      const parentKey = this._uuid6();
      if (flags.hasEngraving) parentProps._engraving_key = `ENG-${parentKey}`;
      if (flags.hasGift) parentProps._package_id = `PKG-${parentKey}`;

      items.push({
        id: parseInt(variantId, 10),
        quantity: 1,
        properties: parentProps
      });

      // Engraving service line item
      if (flags.hasEngraving) {
        const engravingVariantId = await this._lookupVariantId(addons.dataset.engravingHandle);
        const fontLabel = { script: 'Script', serif: 'Serif', sans: 'Block' }[state.engraving.font] || 'Script';
        const metalLabel = {
          'oro_giallo':       'Oro giallo',
          'oro_bianco':       'Oro bianco',
          'oro_rosa':         'Oro rosa',
          'argento':          'Argento',
          'platino':          'Platino',
          'altri_materiali':  'Altri materiali'
        }[state.engraving.metalColor] || 'Argento';
        items.push({
          id: engravingVariantId,
          quantity: 1,
          properties: {
            // 'Da incidere su' e' la riga che boutique e cliente leggono per
            // prima: dice subito su QUALE gioiello va la scritta (il gemello
            // nascosto _for_product resta per la logica del carrello).
            'Da incidere su':    productTitle,
            'Testo da incidere': state.engraving.text.trim(),
            'Stile':             fontLabel,
            _finitura:           metalLabel,
            _for_engraving_key:  `ENG-${parentKey}`,
            _for_product:        productTitle
          }
        });
      }

      // Gift package line item (Task 14 estende con foto+dedica)
      if (flags.hasGift) {
        const giftVariantId = await this._lookupVariantId(addons.dataset.giftHandle);
        const giftProps = {
          'Contiene': productTitle,
          _package_id: `PKG-${parentKey}`
        };
        // Niente riga "Dedica:" vuota nel riepilogo se il cliente non l'ha scritta.
        const dedica = state.gift.dedication.trim();
        if (dedica) giftProps['Dedica'] = dedica;
        if (state.gift.photoUrl) {
          giftProps['Foto polaroid'] = state.gift.photoUrl;
          giftProps._photo_public_id = state.gift.photoPublicId;
        }
        items.push({
          id: giftVariantId,
          quantity: 1,
          properties: giftProps
        });
      }

      const cartDrawer = document.querySelector('cart-drawer');
      const sectionIds = cartDrawer?.getSectionsToRender?.().map((s) => s.id) || [];

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          items,
          sections: sectionIds,
          sections_url: window.location.pathname,
        })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || `HTTP ${res.status}`);
      }
      const data = await res.json();
      addons._aggiunto = true; // abilita il reset della checklist alla rimozione dal carrello
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'add_to_cart_with_addons', {
          has_engraving: flags.hasEngraving,
          has_gift: flags.hasGift
        });
      }
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'cz-addons' } }));
      // Render del drawer con il response pieno (sections + items) e open
      if (cartDrawer && typeof cartDrawer.renderContents === 'function' && data.sections) {
        cartDrawer.renderContents(data); // include drawer.open() internamente
        // Patch: il primo render server-side a volte non include la card
        // incisione annidata sotto il line item (race tra POST /cart/add.js
        // sections rendering e il line item engraving service che e' just
        // stato aggiunto). Refetch fresh per garantire il render completo.
        await _refreshCartDrawerSection();
      } else if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      } else {
        window.location.href = '/';
      }
      // Dispatch dopo renderContents/refresh: gli altri listener (manager,
      // drawer Pacchi) si refresheranno sul cart definitivo, non su uno stale.
      document.dispatchEvent(new CustomEvent('cz:cart:updated', { detail: { source: 'cz-addons' } }));
      } catch (err) {
        console.error('[cz-addons] add-to-cart failed:', err);
        CzToast.show(`Errore aggiungendo al carrello: ${err.message}`);
      }
    },

    _uuid6() {
      const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
      let s = '';
      for (let i = 0; i < 6; i++) s += chars[Math.floor(Math.random() * chars.length)];
      return s;
    },

    _variantIdCache: new Map(),
    async _lookupVariantId(handle) {
      if (!handle) throw new Error('Handle prodotto-servizio non configurato in theme settings');
      if (this._variantIdCache.has(handle)) return this._variantIdCache.get(handle);
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) throw new Error(`Prodotto-servizio '${handle}' non trovato`);
      const data = await res.json();
      const variantId = data.variants[0].id;
      this._variantIdCache.set(handle, variantId);
      return variantId;
    }
  };

  document.addEventListener('DOMContentLoaded', () => CzAddonSync.init());

  // ============================================================
  // Qty enforcement: parent con incisione → qty fissa = 1
  // ============================================================
  const CzQtyEnforcer = {
    init() {
      this._enforce();
      document.addEventListener('cart:updated', () => this._enforce());
      // Anche su MutationObserver del cart per intercettare update theme native
      const cart = document.querySelector('[data-cart-items], .cart-items, main-cart-items');
      if (cart) {
        new MutationObserver(() => this._enforce()).observe(cart, { childList: true, subtree: true });
      }
    },

    _enforce() {
      document.querySelectorAll('[data-engraving-key]').forEach((row) => {
        const key = row.dataset.engravingKey;
        if (!key) return;
        const qtyInput = row.querySelector('input[name="updates[]"], input[type="number"][data-quantity]');
        if (!qtyInput) return;
        if (qtyInput.value !== '1') qtyInput.value = '1';
        qtyInput.disabled = true;
        qtyInput.title = "Per più pezzi personalizzati, aggiungili uno alla volta dalla pagina prodotto.";

        // Disabilita anche eventuali +/- buttons del theme
        const plus = row.querySelector('[name="plus"], .quantity__button[data-action="increase"]');
        const minus = row.querySelector('[name="minus"], .quantity__button[data-action="decrease"]');
        if (plus) plus.disabled = true;
        if (minus) minus.disabled = true;
      });
    }
  };

  document.addEventListener('DOMContentLoaded', () => CzQtyEnforcer.init());

  // ============================================================
  // <cz-gift-package-drawer>
  // ============================================================
  class CzGiftPackageDrawer extends HTMLElement {
    constructor() {
      super();
      this.config = { photoUrl: null, photoPublicId: null, dedication: 'Con affetto' };
      this._originSource = null; // 'pdp' | 'cart-upsell' | 'cart-edit' — chi ha richiesto l'apertura
      this._originDetail = null;
      this._currentStep = 'config'; // 'picker' | 'config'
      this._selectedKeys = []; // line item keys selezionati nel picker (cart-upsell)
    }

    connectedCallback() {
      this.dialog = this.querySelector('[data-drawer]');
      this.stepPicker = this.querySelector('[data-step="picker"]');
      this.stepConfig = this.querySelector('[data-step="config"]');
      this.stepDotsConfig = this.querySelector('[data-step-dots]');
      this.stepEyebrow = this.querySelector('[data-step-eyebrow]');
      this.stepTitle = this.querySelector('[data-step-title]');
      this.stepSubtitle = this.querySelector('[data-step-subtitle]');
      this.selectedSummary = this.querySelector('[data-selected-summary]');
      this.pickerList = this.querySelector('[data-picker-list]');
      this.pickerHelper = this.querySelector('[data-picker-helper]');
      this.btnNext = this.querySelector('[data-next]');
      this.btnBack = this.querySelector('[data-back]');
      this.dedicationInput = this.querySelector('[data-dedication-input]');
      this.dedicationCounter = this.querySelector('[data-dedication-counter]');
      this.dedicationPreview = this.querySelector('[data-dedication-preview]');
      this.photoInput = this.querySelector('[data-photo-input]');
      this.photoSlot = this.querySelector('[data-photo-slot]');
      this.uploadState = this.querySelector('[data-upload-state]');
      this.uploadLabel = this.querySelector('[data-upload-label]');
      this.btnConfirm = this.querySelector('[data-confirm]');
      this.btnCancel = this.querySelector('[data-cancel]');
      this.btnClose = this.querySelector('[data-close]');
      this.priceNote = this.querySelector('[data-price-note]');
      this.dedicationMax = parseInt(this.dataset.dedicationMax, 10) || 30;

      // Cache testo originale CTA confirm + nota soglia (rimpiazzati dinamicamente in open()).
      this._originalConfirmText = this.btnConfirm.textContent;
      this._originalPriceNote = this.priceNote?.textContent || '';

      this._wire();
    }

    _wire() {
      // Apertura via event globale (dispatched da cz-product-addons o cart manager)
      document.addEventListener('cz:gift-drawer:open', (e) => this.open(e.detail));

      this.btnClose.addEventListener('click', () => this.close('cancel'));
      this.btnCancel?.addEventListener('click', () => this.close('cancel'));
      this.btnBack?.addEventListener('click', () => this._navigateToStep('picker'));
      this.btnNext?.addEventListener('click', () => this._handlePickerNext());
      this.btnConfirm.addEventListener('click', () => this._confirm());

      // Picker: delegation su click item (toggle selezione)
      this.pickerList?.addEventListener('click', (e) => {
        const item = e.target.closest('[data-picker-item]');
        if (!item) return;
        this._togglePickerItem(item);
      });

      this.dedicationInput.addEventListener('input', () => {
        const val = this.dedicationInput.value;
        this.config.dedication = val;
        this.dedicationCounter.textContent = `${val.length} / ${this.dedicationMax}`;
        this.dedicationCounter.classList.toggle('is-over', val.length > this.dedicationMax);
        this.dedicationPreview.textContent = val || 'Con affetto';
      });

      // Upload — implementato Task 14
      this.photoInput.addEventListener('change', (e) => this._handleFileSelect(e));
      // Stop propagation del `cancel` event del file picker: se l'utente apre
      // la galleria e poi clicca "Annulla", il <input type="file"> dispatcha
      // un cancel event che BUBBLES fino al <dialog> → listener 'cancel' qui
      // sotto chiudeva tutto il drawer Celebra l'amore. Stop qui.
      this.photoInput.addEventListener('cancel', (e) => e.stopPropagation());

      // Esc + click backdrop chiude
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) this.close('cancel');
      });
      this.dialog.addEventListener('cancel', (e) => {
        e.preventDefault();
        this.close('cancel');
      });
    }

    async open(detail = {}) {
      this._originSource = detail.source || 'pdp';
      this._originDetail = detail;
      this._selectedKeys = [];
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'gift_drawer_open', { source: detail.source || 'pdp' });
      }

      // CTA confirm:
      // - 'cart-edit' → 'Aggiorna'
      // - subtotale carrello >= 100 € → 'Aggiungi il pacchetto · in omaggio' + nota "soglia raggiunta"
      // - altrimenti → label originale con prezzo + nota originale
      if (this._originSource === 'cart-edit') {
        this.btnConfirm.textContent = 'Aggiorna';
      } else {
        let subtotal = 0;
        try {
          const cart = await fetch('/cart.js').then((r) => r.json());
          subtotal = cart.items_subtotal_price ?? cart.total_price ?? 0;
        } catch (_err) { /* fallback: usa label originale */ }
        const isOverThreshold = subtotal >= 10000; // 100,00 €
        if (isOverThreshold) {
          this.btnConfirm.textContent = 'Aggiungi il pacchetto';
          if (this.priceNote) {
            this.priceNote.textContent = 'Soglia 100 € raggiunta — pacchetto in omaggio';
            this.priceNote.classList.add('is-met');
          }
        } else {
          this.btnConfirm.textContent = this._originalConfirmText;
          if (this.priceNote) {
            this.priceNote.textContent = this._originalPriceNote;
            this.priceNote.classList.remove('is-met');
          }
        }
      }

      // Precarica config se passata (es. da cart manager "edit pacco")
      if (detail.currentConfig) {
        this.config = { ...this.config, ...detail.currentConfig };
        this.dedicationInput.value = this.config.dedication;
        this.dedicationInput.dispatchEvent(new Event('input'));
        if (this.config.photoUrl) {
          this._renderPhoto(this.config.photoUrl);
        }
      } else {
        // Reset al default
        this.config = { photoUrl: null, photoPublicId: null, dedication: 'Con affetto' };
        this.dedicationInput.value = 'Con affetto';
        this.dedicationInput.dispatchEvent(new Event('input'));
        this._renderPhotoPlaceholder();
      }

      // Cart-upsell mode → mostra step picker. Altri source → step config diretto.
      if (this._originSource === 'cart-upsell') {
        const looseItems = await this._fetchLooseItems();
        if (looseItems.length === 0) {
          CzToast.show('Tutti gli articoli del carrello sono già in un pacchetto.');
          return;
        }
        // Pre-selezione tramite detail.preSelectedKey (es. click "Incarta"
        // su un loose item nel cart drawer): salta lo step picker.
        const preSelected = detail.preSelectedKey
          ? looseItems.find((it) => it.key === detail.preSelectedKey)
          : null;
        if (preSelected) {
          this._selectedKeys = [preSelected.key];
          this._cachedLooseItems = looseItems;
          this._renderSelectedSummary(looseItems);
          this._navigateToStep('config', { showDots: true });
        } else if (looseItems.length === 1) {
          // Edge case: 1 solo loose → auto-select e salta direttamente a config
          this._selectedKeys = [looseItems[0].key];
          this._cachedLooseItems = looseItems;
          this._renderSelectedSummary(looseItems);
          this._navigateToStep('config', { showDots: true });
        } else {
          this._renderPicker(looseItems);
          this._navigateToStep('picker');
        }
      } else {
        this._navigateToStep('config', { showDots: false });
      }

      // iOS: il <dialog> nativo non blocca lo scroll della pagina dietro —
      // tastiera e scroll interno la trascinano giu'. Si ricorda la posizione
      // qui e la si ripristina alla chiusura (listener 'close', una volta sola).
      this._scrollPrima = window.scrollY;
      if (!this._scrollWired) {
        this._scrollWired = true;
        this.dialog.addEventListener('close', () => {
          if (typeof this._scrollPrima === 'number') window.scrollTo(0, this._scrollPrima);
        });
      }
      this.dialog.showModal();
    }

    async _fetchLooseItems() {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      const giftHandle = (document.querySelector('cz-cart-package-manager')?.dataset.giftHandle || '').toLowerCase();
      return cart.items.filter((it) => {
        // Loose = no _package_id E non è un service line (gift/engraving)
        if (it.properties?._package_id) return false;
        if (it.properties?._package_service_for) return false;
        if (it.properties?._for_engraving_key) return false;
        if (giftHandle && it.handle === giftHandle) return false;
        if (it.handle === 'incisione-personalizzata') return false;
        return true;
      });
    }

    _renderPicker(looseItems) {
      this.pickerList.innerHTML = looseItems.map((it) => `
        <li class="cz-gift-picker__item" data-picker-item data-line-key="${it.key}">
          <span class="cz-gift-picker__check" aria-hidden="true"></span>
          <img class="cz-gift-picker__thumb" src="${it.image || ''}" alt="">
          <div class="cz-gift-picker__meta">
            <p class="cz-gift-picker__brand">${this._escapeHTML(it.vendor || '')}</p>
            <h4 class="cz-gift-picker__title">${this._escapeHTML(it.product_title || it.title)}</h4>
          </div>
        </li>
      `).join('');
      this._cachedLooseItems = looseItems;
      this._updatePickerCount();
    }

    _togglePickerItem(itemEl) {
      const lineKey = itemEl.dataset.lineKey;
      const idx = this._selectedKeys.indexOf(lineKey);
      if (idx >= 0) {
        this._selectedKeys.splice(idx, 1);
        itemEl.classList.remove('is-selected');
      } else {
        this._selectedKeys.push(lineKey);
        itemEl.classList.add('is-selected');
      }
      this._updatePickerCount();
    }

    _updatePickerCount() {
      const n = this._selectedKeys.length;
      const noun = n === 1 ? 'articolo selezionato' : 'articoli selezionati';
      this.pickerHelper.textContent = `${n} ${noun}`;
      this.btnNext.disabled = n === 0;
    }

    _handlePickerNext() {
      if (this._selectedKeys.length === 0) return;
      this._renderSelectedSummary(this._cachedLooseItems);
      this._navigateToStep('config', { showDots: true });
    }

    _renderSelectedSummary(looseItems) {
      const selected = looseItems.filter((it) => this._selectedKeys.includes(it.key));
      if (selected.length === 0 || !this.selectedSummary) return;
      const thumbsHTML = selected.map((it) => {
        const alt = this._escapeHTML(it.product_title || it.title || '');
        return it.image
          ? `<span class="cz-gift-summary__thumb"><img src="${this._escapeHTML(it.image)}" alt="${alt}"></span>`
          : '<span class="cz-gift-summary__thumb cz-gift-summary__thumb--empty" aria-hidden="true"></span>';
      }).join('');
      this.selectedSummary.innerHTML = `
        <p class="cz-gift-summary__label">Incarterai insieme</p>
        <div class="cz-gift-summary__thumbs">${thumbsHTML}</div>
      `;
      this.selectedSummary.hidden = false;
    }

    _navigateToStep(name, opts = {}) {
      this._currentStep = name;
      this.stepPicker.hidden = name !== 'picker';
      this.stepConfig.hidden = name !== 'config';
      // Back button: visibile solo in step config quando il flusso ha un picker prima (cart-upsell con >1 item)
      const showBack = name === 'config'
        && this._originSource === 'cart-upsell'
        && (this._cachedLooseItems?.length || 0) > 1;
      if (this.btnBack) this.btnBack.hidden = !showBack;
      // Step dots in config: solo cart-upsell o se opts.showDots forzato
      if (this.stepDotsConfig) {
        this.stepDotsConfig.hidden = !(opts.showDots || this._originSource === 'cart-upsell');
      }
      // Cambia eyebrow in config per cart-upsell mode
      if (name === 'config' && this._originSource === 'cart-upsell' && this.stepEyebrow) {
        this.stepEyebrow.textContent = 'Pacchetto regalo · 2 di 2';
      } else if (name === 'config' && this._originSource === 'cart-edit' && this.stepEyebrow) {
        this.stepEyebrow.textContent = 'Modifica pacchetto';
      } else if (this.stepEyebrow) {
        this.stepEyebrow.textContent = 'Pacchetto regalo';
      }
      // Hidden summary se non cart-upsell
      if (this.selectedSummary && this._originSource !== 'cart-upsell') {
        this.selectedSummary.hidden = true;
      }
    }

    _escapeHTML(str) {
      return String(str || '').replace(/[&<>"']/g, (m) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
      }[m]));
    }

    close(reason = 'cancel') {
      this.dialog.close();
      if (reason === 'cancel') {
        // Notifica al chiamante che l'utente ha annullato (toggle card off)
        document.dispatchEvent(new CustomEvent('cz:gift-drawer:cancel', { detail: this._originDetail }));
      }
    }

    _confirm() {
      // Validazione
      if (!this.config.dedication.trim()) {
        CzToast.show('Inserisci una dedica.');
        return;
      }
      if (this.config.dedication.length > this.dedicationMax) {
        CzToast.show(`La dedica supera il limite di ${this.dedicationMax} caratteri.`);
        return;
      }
      // Emit risultato
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'gift_drawer_confirm', {
          source: this._originSource,
          has_photo: !!this.config.photoUrl,
          dedication_length: this.config.dedication.length,
          selected_count: this._selectedKeys.length
        });
      }
      document.dispatchEvent(new CustomEvent('cz:gift-drawer:confirm', {
        detail: {
          source: this._originSource,
          origin: this._originDetail,
          config: { ...this.config },
          // Solo per cart-upsell: line keys dei prodotti da legare al nuovo pacco
          selectedLineKeys: this._originSource === 'cart-upsell' ? [...this._selectedKeys] : undefined
        }
      }));
      this.dialog.close();
    }

    _renderPhoto(url) {
      if (!url) return;
      // Costruzione DOM esplicita per logging errori (Cloudinary CORS, 404, ecc.)
      const img = document.createElement('img');
      img.alt = 'Foto polaroid';
      img.onload = () => console.log('[gift-drawer] foto polaroid caricata:', url);
      img.onerror = () => {
        console.error('[gift-drawer] foto polaroid NON caricata. URL:', url);
        if (window.CzToast) window.CzToast.show('Anteprima foto non caricata. Verifica connessione.');
      };
      img.src = url;
      this.photoSlot.innerHTML = '';
      this.photoSlot.appendChild(img);
    }

    _renderPhotoPlaceholder() {
      this.photoSlot.innerHTML = '<span class="cz-polaroid__placeholder">📷</span>';
    }

    async _handleFileSelect(e) {
      const file = e.target.files[0];
      if (!file) return;

      // Validazione
      const okFormats = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
      if (!okFormats.includes(file.type) && !file.name.match(/\.(jpe?g|png|webp|heic|heif)$/i)) {
        CzToast.show('Formato non supportato. Usa JPG, PNG, WEBP o HEIC.');
        e.target.value = '';
        return;
      }
      if (file.size > 8 * 1024 * 1024) {
        CzToast.show('File troppo grande (max 8 MB).');
        e.target.value = '';
        return;
      }

      this._setUploadState('Caricamento…', true);

      try {
        // 1. HEIC → JPG se serve
        let blob = file;
        if (file.type.startsWith('image/heic') || file.type.startsWith('image/heif') || /\.heic|\.heif$/i.test(file.name)) {
          if (typeof window.heic2any === 'undefined') {
            throw new Error('Libreria HEIC non caricata. Riprova tra un momento.');
          }
          blob = await window.heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
        }

        // 2. Resize max 2000px lato lungo, quality 0.85
        const resized = await this._resizeImage(blob, 2000, 0.85);

        // 3. Upload Cloudinary
        const cloud = this.dataset.cloudinaryCloud;
        const preset = this.dataset.cloudinaryPreset;
        if (!cloud || !preset) {
          throw new Error('Cloudinary non configurato. Vedi theme settings.');
        }
        const formData = new FormData();
        formData.append('file', resized);
        formData.append('upload_preset', preset);

        const res = await fetch(`https://api.cloudinary.com/v1_1/${cloud}/image/upload`, {
          method: 'POST',
          body: formData
        });
        if (!res.ok) throw new Error(`Upload fallito (${res.status})`);
        const data = await res.json();

        // 4. Save state + render preview
        this.config.photoUrl = data.secure_url;
        this.config.photoPublicId = data.public_id;
        this._renderPhoto(data.secure_url);
        this._setUploadState('Caricata ✓', false);
      } catch (err) {
        console.error('[gift-drawer] upload failed:', err);
        CzToast.show(`Caricamento non riuscito: ${err.message}`);
        this._setUploadState('', false);
        e.target.value = '';
      }
    }

    _resizeImage(blob, maxSide, quality) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(blob);
        img.onload = () => {
          URL.revokeObjectURL(url);
          let { width, height } = img;
          if (width > maxSide || height > maxSide) {
            const ratio = Math.min(maxSide / width, maxSide / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);
          canvas.toBlob((b) => b ? resolve(b) : reject(new Error('Resize failed')), 'image/jpeg', quality);
        };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Impossibile leggere immagine')); };
        img.src = url;
      });
    }

    _setUploadState(text, busy) {
      this.uploadState.textContent = text;
      this.uploadState.hidden = !text;
      this.uploadLabel.style.cursor = busy ? 'wait' : 'pointer';
      this.btnConfirm.disabled = busy;
    }
  }

  // ============================================================
  // <cz-engraving-drawer>
  // ============================================================
  class CzEngravingDrawer extends HTMLElement {
    constructor() {
      super();
      this.state = { text: '', font: 'script', metalColor: 'argento' };
      this._originSource = null;
      this._originDetail = null;
      // U+FE0E (variation selector-15) consentito: viene appeso ai simboli
      // (♥∞◆★) per forzarne il text-presentation monocromo invece dell'emoji color.
      this._sanitizeRegex = /[^\p{L}\p{N}\s\.,'\-♥∞◆★&\u{FE0E}]/gu;
      this._metalLabel = {
        'oro_giallo':       'Oro giallo',
        'oro_bianco':       'Oro bianco',
        'oro_rosa':         'Oro rosa',
        'argento':          'Argento',
        'platino':          'Platino',
        'altri_materiali':  'Altri materiali'
      };
      this._metalClass = {
        'oro_giallo':       'cz-eng-preview--gold-yellow',
        'oro_bianco':       'cz-eng-preview--silver',
        'oro_rosa':         'cz-eng-preview--gold-rose',
        'argento':          'cz-eng-preview--silver',
        'platino':          'cz-eng-preview--silver',
        'altri_materiali':  'cz-eng-preview--silver'
      };
    }

    connectedCallback() {
      this.dialog = this.querySelector('[data-drawer]');
      this.input = this.querySelector('[data-engraving-input]');
      this.counter = this.querySelector('[data-engraving-counter]');
      this.preview = this.querySelector('[data-preview]');
      this.previewText = this.querySelector('[data-preview-text]');
      this.picker = this.querySelectorAll('.cz-eng-picker__chip');
      this.btnConfirm = this.querySelector('[data-confirm]');
      this.btnCancel = this.querySelector('[data-cancel]');
      this.btnClose = this.querySelector('[data-close]');
      this.priceNote = this.querySelector('[data-price-note]');
      this.maxChars = parseInt(this.dataset.engravingMax, 10) || 40;
      this.defaultMetal = this.dataset.defaultMetal || 'argento';
      // Cache testi originali CTA + nota (rimpiazzati dinamicamente in open()).
      this._originalConfirmText = this.btnConfirm.textContent;
      this._originalPriceNote = this.priceNote?.textContent || '';
      this._wire();
    }

    _wire() {
      document.addEventListener('cz:engraving-drawer:open', (e) => this.open(e.detail));
      this.btnClose.addEventListener('click', () => this.close('cancel'));
      this.btnCancel.addEventListener('click', () => this.close('cancel'));
      this.btnConfirm.addEventListener('click', () => this._confirm());
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) this.close('cancel');
      });
      this.dialog.addEventListener('cancel', (e) => {
        e.preventDefault();
        this.close('cancel');
      });
      this._wirePickerAndInput();
      this._wireSymbols();
    }

    async open(detail = {}) {
      this._originSource = detail.source || 'pdp';
      this._originDetail = detail;
      const hasUserConfig = !!(detail.currentConfig && detail.currentConfig.active);
      const metalColor = (hasUserConfig && detail.currentConfig.metalColor)
        || detail.metalColor
        || this.defaultMetal;
      const text = (hasUserConfig && detail.currentConfig.text) || '';
      const font = (hasUserConfig && detail.currentConfig.font) || 'script';
      this.state = { text, font, metalColor };
      this._applyMetalClass(metalColor);
      this._applyFontSelection(font);
      this.input.value = text;
      this._updatePreviewAndCounter();
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'engraving_drawer_open', { source: this._originSource });
      }

      // CTA confirm:
      // - source='cart-edit' → 'Aggiorna' (no pricing/soglia)
      // - subtotale carrello >= 100 € → 'Conferma incisione' + nota gold soglia raggiunta
      // - altrimenti → label originale con prezzo + nota originale
      if (this._originSource === 'cart-edit') {
        this.btnConfirm.textContent = 'Aggiorna';
      } else {
        let subtotal = 0;
        try {
          const cart = await fetch('/cart.js').then((r) => r.json());
          subtotal = cart.items_subtotal_price ?? cart.total_price ?? 0;
        } catch (_err) { /* fallback: label originale */ }
        const isOverThreshold = subtotal >= 10000; // 100,00 €
        if (isOverThreshold) {
          this.btnConfirm.textContent = 'Conferma incisione';
          if (this.priceNote) {
            this.priceNote.textContent = 'Soglia 100 € raggiunta — incisione in omaggio';
            this.priceNote.classList.add('is-met');
          }
        } else {
          this.btnConfirm.textContent = this._originalConfirmText;
          if (this.priceNote) {
            this.priceNote.textContent = this._originalPriceNote;
            this.priceNote.classList.remove('is-met');
          }
        }
      }

      // iOS: il <dialog> nativo non blocca lo scroll della pagina dietro —
      // tastiera e scroll interno la trascinano giu'. Si ricorda la posizione
      // qui e la si ripristina alla chiusura (listener 'close', una volta sola).
      this._scrollPrima = window.scrollY;
      if (!this._scrollWired) {
        this._scrollWired = true;
        this.dialog.addEventListener('close', () => {
          if (typeof this._scrollPrima === 'number') window.scrollTo(0, this._scrollPrima);
        });
      }
      this.dialog.showModal();
    }

    close(reason = 'cancel') {
      this.dialog.close();
      if (reason === 'cancel') {
        document.dispatchEvent(new CustomEvent('cz:engraving-drawer:cancel', { detail: this._originDetail }));
      }
    }

    _applyFontSelection(font) {
      this.state.font = font;
      this.picker.forEach((chip) => {
        const isSel = chip.dataset.font === font;
        chip.classList.toggle('is-selected', isSel);
        chip.setAttribute('aria-checked', String(isSel));
      });
      this.previewText.classList.remove('cz-eng-font-script', 'cz-eng-font-serif', 'cz-eng-font-sans');
      this.previewText.classList.add('cz-eng-font-' + font);
    }

    _wirePickerAndInput() {
      this.picker.forEach((chip) => {
        chip.addEventListener('click', () => {
          this._applyFontSelection(chip.dataset.font);
        });
      });

      this.input.addEventListener('input', () => {
        const raw = this.input.value;
        const clean = raw.replace(this._sanitizeRegex, '');
        if (clean !== raw) {
          const pos = this.input.selectionStart;
          this.input.value = clean;
          try { this.input.setSelectionRange(pos - (raw.length - clean.length), pos - (raw.length - clean.length)); } catch (_) {}
        }
        this.state.text = this.input.value;
        this._updatePreviewAndCounter();
      });
    }

    _wireSymbols() {
      this.querySelectorAll('[data-insert-symbol]').forEach((btn) => {
        btn.addEventListener('click', () => this._insertSymbol(btn.dataset.insertSymbol));
      });
    }

    _insertSymbol(symbol) {
      const input = this.input;
      const start = input.selectionStart ?? input.value.length;
      const end = input.selectionEnd ?? input.value.length;
      const newValue = input.value.slice(0, start) + symbol + input.value.slice(end);
      const newVisibleLength = newValue.replace(/\u{FE0E}/gu, '').length;
      if (newVisibleLength > this.maxChars) {
        if (window.CzToast) window.CzToast.show(`Hai raggiunto il limite di ${this.maxChars} caratteri.`);
        return;
      }
      input.value = newValue;
      const newPos = start + symbol.length;
      try { input.setSelectionRange(newPos, newPos); } catch (_) {}
      input.focus();
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    _updatePreviewAndCounter() {
      const text = this.state.text;
      // I VS-15 (U+FE0E) sono zero-width: non contano per il limite di caratteri.
      const visibleLength = text.replace(/\u{FE0E}/gu, '').length;
      this.counter.textContent = `${visibleLength} / ${this.maxChars}`;
      this.counter.classList.toggle('is-over', visibleLength > this.maxChars);
      this.previewText.textContent = text.length > 0 ? text : 'Anteprima incisione';
      this.previewText.style.opacity = text.length > 0 ? '1' : '0.5';
    }

    _applyMetalClass(metalColor) {
      const cls = this._metalClass[metalColor] || 'cz-eng-preview--silver';
      ['cz-eng-preview--gold-yellow','cz-eng-preview--gold-rose','cz-eng-preview--silver','cz-eng-preview--titanium']
        .forEach((c) => this.preview.classList.remove(c));
      this.preview.classList.add(cls);
      this.state.metalColor = this._metalLabel[metalColor] ? metalColor : 'argento';
    }

    _confirm() {
      const text = this.state.text.trim();
      const visibleLength = text.replace(/\u{FE0E}/gu, '').length;
      if (!text) {
        if (window.CzToast) window.CzToast.show('Inserisci il testo da incidere.');
        return;
      }
      if (visibleLength > this.maxChars) {
        if (window.CzToast) window.CzToast.show(`Il testo supera il limite di ${this.maxChars} caratteri.`);
        return;
      }
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'engraving_drawer_confirm', {
          source: this._originSource,
          font: this.state.font,
          metal: this.state.metalColor,
          text_length: visibleLength
        });
      }
      document.dispatchEvent(new CustomEvent('cz:engraving-drawer:confirm', {
        detail: {
          source: this._originSource,
          origin: this._originDetail,
          config: { text, font: this.state.font, metalColor: this.state.metalColor }
        }
      }));
      this.dialog.close();
    }
  }

  customElements.define('cz-engraving-drawer', CzEngravingDrawer);

  customElements.define('cz-gift-package-drawer', CzGiftPackageDrawer);

  // Ascolta il confirm e propaga alla <cz-product-addons> sorgente (per il flusso PDP)
  document.addEventListener('cz:gift-drawer:confirm', (e) => {
    if (e.detail.source !== 'pdp') return;
    const handle = e.detail.origin?.productHandle;
    const addons = document.querySelector(`cz-product-addons[data-product-handle="${handle}"]`);
    if (addons) addons.setGiftConfig(e.detail.config);
  });

  document.addEventListener('cz:gift-drawer:cancel', (e) => {
    if (!e.detail || e.detail.source !== 'pdp') return;
    const handle = e.detail.productHandle;
    const addons = document.querySelector(`cz-product-addons[data-product-handle="${handle}"]`);
    if (!addons) return;
    // Riporta card gift a OFF se cliente ha cancellato senza configurare
    if (!addons.state.gift.photoUrl && !addons.state.gift.dedication.trim()) {
      const card = addons.querySelector('[data-addon="gift"]');
      if (card) {
        card.classList.remove('is-active');
        card.setAttribute('aria-pressed', 'false');
      }
      addons.state.gift.active = false;
    }
  });

  // Engraving drawer → propaga config alla <cz-product-addons> sorgente
  document.addEventListener('cz:engraving-drawer:confirm', (e) => {
    if (e.detail.source !== 'pdp') return;
    const handle = e.detail.origin && e.detail.origin.productHandle;
    const addons = document.querySelector(`cz-product-addons[data-product-handle="${handle}"]`);
    if (addons) addons.setEngravingConfig(e.detail.config);
  });

  document.addEventListener('cz:engraving-drawer:cancel', (e) => {
    if (!e.detail || e.detail.source !== 'pdp') return;
    const handle = e.detail.productHandle;
    const addons = document.querySelector(`cz-product-addons[data-product-handle="${handle}"]`);
    if (!addons) return;
    // Riporta card a OFF se cliente ha cancellato senza configurare per la prima volta
    if (!addons.state.engraving.text.trim()) {
      const card = addons.querySelector('[data-addon="engraving"]');
      if (card) {
        card.classList.remove('is-active');
        card.setAttribute('aria-pressed', 'false');
      }
      addons.state.engraving.active = false;
    }
  });

  // ============================================================
  // CzPackageOps — operazioni move/split/merge/remove/edit sui pacchi
  // ============================================================
  const CzPackageOps = {
    cart: null,
    history: 'cz_pkg_hist',
    _variantIdCache: new Map(),

    async refresh() {
      const res = await fetch('/cart.js');
      this.cart = await res.json();
      return this.cart;
    },

    snapshot() {
      if (!this.cart) return { packages: [], unpackaged: [] };
      const giftHandle = document.querySelector('cz-cart-package-manager')?.dataset.giftHandle;
      const packagesById = new Map();
      const unpackaged = [];

      this.cart.items.forEach((item) => {
        if (item.handle === giftHandle && item.properties && item.properties._package_id) {
          packagesById.set(item.properties._package_id, {
            id: item.properties._package_id,
            serviceLineKey: item.key,
            servicePrice: item.price,
            photoUrl: item.properties['Foto polaroid'] || null,
            photoPublicId: item.properties._photo_public_id || null,
            dedication: item.properties['Dedica'] || '',
            items: []
          });
        }
      });

      this.cart.items.forEach((item) => {
        if (item.handle === giftHandle) return;
        const pkgId = item.properties && item.properties._package_id;
        if (pkgId && packagesById.has(pkgId)) {
          packagesById.get(pkgId).items.push(item);
        } else if (!item.properties || !item.properties._for_engraving_key) {
          unpackaged.push(item);
        }
      });

      return {
        packages: Array.from(packagesById.values()),
        unpackaged
      };
    },

    async moveItemToPackage(lineKey, targetPackageId) {
      const item = this.cart.items.find((it) => it.key === lineKey);
      if (!item) throw new Error('Line item non trovato');
      const newProps = { ...(item.properties || {}), _package_id: targetPackageId };
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, properties: newProps })
      });
      await this.refresh();
      await this._refreshArticlesIncluded();
      await this._cleanupEmptyPackages();
    },

    async removeItemFromPackage(lineKey) {
      const item = this.cart.items.find((it) => it.key === lineKey);
      if (!item) throw new Error('Line item non trovato');
      const newProps = { ...(item.properties || {}) };
      delete newProps._package_id;
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, properties: newProps })
      });
      await this.refresh();
      await this._refreshArticlesIncluded();
      await this._cleanupEmptyPackages();
    },

    async createEmptyPackage() {
      const newId = 'PKG-' + this._uuid6();
      const giftHandle = document.querySelector('cz-cart-package-manager').dataset.giftHandle;
      const variantId = await this._lookupVariantId(giftHandle);
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1,
            properties: {
              'Contiene': '—',
              'Dedica': 'Con affetto',
              _package_id: newId
            }
          }]
        })
      });
      await this.refresh();
      return newId;
    },

    async _refreshArticlesIncluded() {
      const snap = this.snapshot();
      for (const pkg of snap.packages) {
        const titles = pkg.items.map((it) => it.product_title).join(', ') || '—';
        const service = this.cart.items.find((it) => it.key === pkg.serviceLineKey);
        if (!service) continue;
        const currentTitles = service.properties && (service.properties['Contiene'] || service.properties['Articoli inclusi']);
        if (currentTitles === titles) continue;
        const newProps = { ...service.properties, 'Contiene': titles };
        delete newProps['Articoli inclusi']; // migra le righe nate col vecchio nome
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: pkg.serviceLineKey, properties: newProps })
        });
      }
      await this.refresh();
    },

    async _cleanupEmptyPackages() {
      const snap = this.snapshot();
      for (const pkg of snap.packages) {
        if (pkg.items.length === 0) {
          this._saveHistory(pkg.id, pkg.photoUrl, pkg.photoPublicId, pkg.dedication);
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: pkg.serviceLineKey, quantity: 0 })
          });
        }
      }
      await this.refresh();
    },

    async purgeOrphans() {
      let rimossi = 0;
      await this.refresh();
      const giftHandle = document.querySelector('cz-cart-package-manager')?.dataset.giftHandle;
      const allPackageIds = new Set();   // package_id che hanno almeno 1 PRODOTTO reale
      const servicePackageIds = new Set(); // package_id presenti sui service line
      const allEngravingKeys = new Set();
      this.cart.items.forEach((it) => {
        if (it.properties?._package_id && it.handle !== giftHandle) allPackageIds.add(it.properties._package_id);
        if (it.properties?._package_id && it.handle === giftHandle) servicePackageIds.add(it.properties._package_id);
        if (it.properties?._engraving_key) allEngravingKeys.add(it.properties._engraving_key);
      });

      for (const item of this.cart.items) {
        const isOrphanGift = item.handle === giftHandle && item.properties?._package_id && !allPackageIds.has(item.properties._package_id);
        const isOrphanEngraving = item.properties?._for_engraving_key && !allEngravingKeys.has(item.properties._for_engraving_key.replace('ENG-', 'ENG-'));
        // Item con _package_id che NON ha un service line corrispondente:
        // il pacchetto è stato eliminato ma l'item è rimasto orfano. Il template
        // cart-drawer.liquid skippa item con _package_id != blank → l'articolo
        // sparisce dalla vista. Rimuovo il _package_id per liberarlo.
        const isOrphanItem = item.handle !== giftHandle
          && item.properties?._package_id
          && !servicePackageIds.has(item.properties._package_id)
          && !item.properties?._for_engraving_key;
        if (isOrphanGift) this._saveHistory(item.properties._package_id, item.properties['Foto polaroid'], item.properties._photo_public_id, item.properties['Dedica']);
        if (isOrphanGift || isOrphanEngraving) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: item.key, quantity: 0 })
          });
          rimossi++;
        } else if (isOrphanItem) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: item.key,
              quantity: item.quantity,
              properties: { ...(item.properties || {}), _package_id: '' },
            }),
          });
        }
      }
      await this.refresh();
      return rimossi;
    },

    _saveHistory(packageId, photoUrl, photoPublicId, dedication) {
      try {
        const all = JSON.parse(localStorage.getItem(this.history) || '{}');
        all[packageId] = { photoUrl, photoPublicId, dedication, savedAt: Date.now() };
        const cutoff = Date.now() - 7 * 24 * 3600 * 1000;
        Object.keys(all).forEach((k) => { if (all[k].savedAt < cutoff) delete all[k]; });
        localStorage.setItem(this.history, JSON.stringify(all));
      } catch (e) { /* localStorage disabled */ }
    },

    _getHistory(packageId) {
      try {
        const all = JSON.parse(localStorage.getItem(this.history) || '{}');
        return all[packageId] || null;
      } catch (e) { return null; }
    },

    _uuid6() {
      const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
      let s = '';
      for (let i = 0; i < 6; i++) s += chars[Math.floor(Math.random() * chars.length)];
      return s;
    },

    async _lookupVariantId(handle) {
      if (this._variantIdCache.has(handle)) return this._variantIdCache.get(handle);
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) throw new Error(`Prodotto-servizio '${handle}' non trovato`);
      const data = await res.json();
      const variantId = data.variants[0].id;
      this._variantIdCache.set(handle, variantId);
      return variantId;
    }
  };

  // ============================================================
  // <cz-cart-package-manager>
  // ============================================================
  class CzCartPackageManager extends HTMLElement {
    constructor() {
      super();
    }

    async connectedCallback() {
      this.summary = this.querySelector('[data-summary]');
      this.countLabel = this.querySelector('[data-count-label]');
      this.openBtn = this.querySelector('[data-open-manager]');
      // Nota: il drawer "tavolo da incarto" (data-manager-drawer / data-close /
      // data-manager-body) è stato estratto in <cz-cart-package-drawer> (sezione
      // globale), non vive più dentro <cz-cart-package-manager>. Il manager
      // dispatch solo l'evento, il drawer ascolta e si apre da solo.

      if (this.openBtn) {
        this.openBtn.addEventListener('click', () => {
          document.dispatchEvent(new CustomEvent('cz:package-drawer:open'));
        });
      }

      // Ascolta entrambi i custom event: cart:updated (legacy, dispatched dal
      // wizard cart-upsell + cart.js Dawn override) E cz:cart:updated
      // (dispatched dai handler riorganizza in cz-cart-package-drawer e altri).
      // Senza il secondo, dopo Sposta/Aggiungi/Crea pacco il banner restava
      // in stato "Lo regali a qualcuno?" anche se il pacco esisteva.
      document.addEventListener('cart:updated', () => this._refresh());
      document.addEventListener('cz:cart:updated', () => this._refresh());
      await this._refresh();
    }

    async _refresh() {
      await CzPackageOps.refresh();
      const snap = CzPackageOps.snapshot();
      const count = snap.packages.length;

      const stateEmpty = this.querySelector('[data-state-empty]');
      const stateActive = this.querySelector('[data-state-active]');
      const noun = this.querySelector('[data-count-noun]');
      const addPkgBtn = this.querySelector('[data-add-package]');

      this.summary.hidden = false;

      if (count === 0) {
        if (stateEmpty) stateEmpty.hidden = false;
        if (stateActive) stateActive.hidden = true;
        if (addPkgBtn && !addPkgBtn._wired) {
          addPkgBtn._wired = true;
          addPkgBtn.addEventListener('click', () => {
            document.dispatchEvent(new CustomEvent('cz:gift-drawer:open', {
              detail: { source: 'cart-upsell' }
            }));
          });
        }
        return;
      }

      if (stateEmpty) stateEmpty.hidden = true;
      if (stateActive) stateActive.hidden = false;
      if (this.countLabel) this.countLabel.textContent = count;
      if (noun) noun.textContent = count === 1 ? 'pacchetto regalo' : 'pacchetti regalo';

      // Riga "aggiungi un altro pacchetto / crea": ha senso solo se ci sono
      // articoli liberi da incartare. Se tutti sono già in un pacco, nascondi.
      const secondaryRow = this.querySelector('.cz-cart-pkg__main--secondary');
      if (secondaryRow) secondaryRow.hidden = snap.unpackaged.length === 0;

      // Wire del CTA secondario "crea un altro pacchetto" (presente solo nello
      // stato attivo). Stesso behavior di addPkgBtn ma triggerato quando già
      // esistono pacchi.
      const createAnotherBtn = this.querySelector('[data-create-another]');
      if (createAnotherBtn && !createAnotherBtn._wired) {
        createAnotherBtn._wired = true;
        createAnotherBtn.addEventListener('click', () => {
          document.dispatchEvent(new CustomEvent('cz:gift-drawer:open', {
            detail: { source: 'cart-upsell' }
          }));
        });
      }
    }

    async _openDrawer() {
      await this._renderManager();
      this.drawer.showModal();
    }

    async _renderManager() {
      const snap = CzPackageOps.snapshot();
      let html = '';
      snap.packages.forEach((pkg, i) => {
        html += `<div class="cz-pkg-block" data-pkg-id="${pkg.id}">
          <div class="cz-pkg-block__header">
            <span class="cz-pkg-block__title">📦 Pacco ${i + 1}</span>
            <button class="cz-pkg-block__edit" data-edit-pkg="${pkg.id}">Modifica foto/dedica</button>
          </div>
          ${pkg.items.map((it) => `
            <div class="cz-pkg-item" data-line-key="${it.key}">
              <span>${it.product_title}</span>
              <span class="cz-pkg-item__actions">
                <button class="cz-pkg-item__btn" data-move="${it.key}" data-from-pkg="${pkg.id}">Sposta</button>
                <button class="cz-pkg-item__btn" data-remove-from-pkg="${it.key}" data-from-pkg-id="${pkg.id}">Togli dal pacco</button>
              </span>
            </div>
          `).join('')}
        </div>`;
      });
      html += `<button class="cz-pkg-new-btn" data-create-pkg>+ Crea un nuovo pacco vuoto</button>`;
      if (snap.unpackaged.length > 0) {
        html += `<p class="cz-pkg-section-label">Articoli senza pacco</p>`;
        snap.unpackaged.forEach((it) => {
          html += `<div class="cz-pkg-item" data-line-key="${it.key}">
            <span>${it.product_title}</span>
            <button class="cz-pkg-item__btn" data-add-to-pkg="${it.key}">+ Aggiungi a pacco</button>
          </div>`;
        });
      }
      this.body.innerHTML = html;
      this._wireActions();
    }

    _wireActions() {
      this.body.querySelectorAll('[data-move]').forEach((btn) => {
        btn.addEventListener('click', () => this._handleMoveClick(btn));
      });
      this.body.querySelectorAll('[data-remove-from-pkg]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const key = btn.dataset.removeFromPkg;
          await CzPackageOps.removeItemFromPackage(key);
          await this._renderManager();
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'package-manager' } }));
        });
      });
      this.body.querySelectorAll('[data-create-pkg]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          await CzPackageOps.createEmptyPackage();
          await this._renderManager();
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'package-manager' } }));
        });
      });
      this.body.querySelectorAll('[data-add-to-pkg]').forEach((btn) => {
        btn.addEventListener('click', () => this._handleAddToPkgClick(btn));
      });
      this.body.querySelectorAll('[data-edit-pkg]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const pkgId = btn.dataset.editPkg;
          const snap = CzPackageOps.snapshot();
          const pkg = snap.packages.find((p) => p.id === pkgId);
          if (!pkg) return;
          document.dispatchEvent(new CustomEvent('cz:gift-drawer:open', {
            detail: {
              source: 'cart-edit',
              packageId: pkgId,
              serviceLineKey: pkg.serviceLineKey,
              currentConfig: {
                photoUrl: pkg.photoUrl,
                photoPublicId: pkg.photoPublicId,
                dedication: pkg.dedication
              }
            }
          }));
        });
      });
    }

    _showInlineMenu(btn, options, onPick) {
      const id = `cz-menu-${Date.now()}`;
      const html = `<div id="${id}" style="position:fixed;background:#fff;border:1px solid #ccc;padding:10px;z-index:99999;box-shadow:0 4px 16px rgba(0,0,0,.15);">
        <p style="margin:0 0 6px;font-size:11px;color:#888;">Sposta in:</p>
        ${options.map((opt) => `<button type="button" data-target="${opt.value}" style="display:block;width:100%;padding:6px;margin:2px 0;background:${opt.value === '__new__' ? '#fff' : '#faf7f1'};border:${opt.value === '__new__' ? '1.5px dashed' : '1px solid'} #c9a86b;cursor:pointer;font-size:12px;color:${opt.value === '__new__' ? '#c9a86b' : 'inherit'};">${opt.label}</button>`).join('')}
      </div>`;
      const rect = btn.getBoundingClientRect();
      document.body.insertAdjacentHTML('beforeend', html);
      const menu = document.getElementById(id);
      menu.style.top = `${rect.bottom + 4}px`;
      menu.style.left = `${rect.left}px`;
      menu.querySelectorAll('[data-target]').forEach((b) => {
        b.addEventListener('click', async () => {
          const target = b.dataset.target;
          menu.remove();
          await onPick(target);
        });
      });
      setTimeout(() => {
        document.addEventListener('click', function close(e) {
          if (!menu.contains(e.target)) { menu.remove(); document.removeEventListener('click', close); }
        });
      }, 0);
    }

    _handleMoveClick(btn) {
      const lineKey = btn.dataset.move;
      const fromPkg = btn.dataset.fromPkg;
      const snap = CzPackageOps.snapshot();
      const otherPkgs = snap.packages.filter((p) => p.id !== fromPkg);
      const options = otherPkgs.map((p) => ({ value: p.id, label: `Pacco ${snap.packages.indexOf(p) + 1}` }));
      options.push({ value: '__new__', label: '+ Crea nuovo pacco' });
      this._showInlineMenu(btn, options, async (target) => {
        let pkgId = target;
        if (target === '__new__') pkgId = await CzPackageOps.createEmptyPackage();
        await CzPackageOps.moveItemToPackage(lineKey, pkgId);
        await this._renderManager();
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'package-manager' } }));
      });
    }

    _handleAddToPkgClick(btn) {
      const lineKey = btn.dataset.addToPkg;
      const snap = CzPackageOps.snapshot();
      const options = snap.packages.map((p, i) => ({ value: p.id, label: `Pacco ${i + 1}` }));
      options.push({ value: '__new__', label: '+ Crea nuovo pacco' });
      this._showInlineMenu(btn, options, async (target) => {
        let pkgId = target;
        if (target === '__new__') pkgId = await CzPackageOps.createEmptyPackage();
        await CzPackageOps.moveItemToPackage(lineKey, pkgId);
        await this._renderManager();
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'package-manager' } }));
      });
    }
  }

  customElements.define('cz-cart-package-manager', CzCartPackageManager);

  // Handler confirm per source='cart-upsell': crea il line item servizio e
  // collega i prodotti selezionati al nuovo pacco via _package_id.
  document.addEventListener('cz:gift-drawer:confirm', async (e) => {
    if (e.detail.source !== 'cart-upsell') return;
    const { config, selectedLineKeys } = e.detail;
    if (!selectedLineKeys || selectedLineKeys.length === 0) {
      CzToast.show('Nessun articolo selezionato.');
      return;
    }
    try {
      // Lookup variant id del gift service product
      const giftHandle = document.querySelector('cz-cart-package-manager')?.dataset.giftHandle;
      if (!giftHandle) throw new Error('Gift package handle non configurato');
      const productRes = await fetch(`/products/${giftHandle}.js`);
      if (!productRes.ok) throw new Error('Prodotto servizio pacchetto non trovato');
      const product = await productRes.json();
      const variantId = product.variants[0].id;

      // Genera package_id unico (6 char alphanumeric, stessa logica di CzAddonSync._uuid6)
      const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
      let pkgId = 'PKG-';
      for (let i = 0; i < 6; i++) pkgId += chars[Math.floor(Math.random() * chars.length)];

      // Snapshot cart per ricavare titoli articoli inclusi + variant IDs.
      // Salviamo i variant_id dei prodotti selezionati perché dopo l'add.js
      // i lineKey degli item esistenti possono cambiare (Shopify rigenera
      // gli ID), mentre i variant_id sono stabili → li usiamo come ponte
      // per ritrovare i nuovi lineKey post-add.js.
      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();
      const selectedItems = cart.items.filter((it) => selectedLineKeys.includes(it.key));
      const selectedVariantIds = selectedItems.map((it) => it.variant_id || it.id);
      const titlesText = selectedItems.map((it) => it.product_title || it.title).join(', ');

      // 1. Aggiungi service line item (il "fee" pacchetto + foto/dedica)
      const props = {
        'Contiene': titlesText,
        'Dedica': config.dedication.trim(),
        _package_id: pkgId,
      };
      if (config.photoUrl) {
        props['Foto polaroid'] = config.photoUrl;
        props._photo_public_id = config.photoPublicId;
      }
      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1, properties: props }]
        })
      });
      if (!addRes.ok) {
        const err = await addRes.json().catch(() => ({}));
        throw new Error(err.description || `Add fallito (${addRes.status})`);
      }

      // 2. Re-fetch cart.js per ottenere i lineKey FRESCHI post-add.js.
      // Risolviamo via variant_id (stabile) — il lineKey è "variant_id:hash"
      // e l'hash può cambiare dopo modifiche al cart.
      const cartAfterRes = await fetch('/cart.js');
      const cartAfter = await cartAfterRes.json();
      const freshKeysByVariantId = new Map();
      for (const it of cartAfter.items) {
        const vId = it.variant_id || it.id;
        // Skip service lines: i prodotti hanno il loro variant, il service
        // gift-wrap-2 ne ha uno diverso e non lo confondiamo.
        if (it.handle === giftHandle) continue;
        if (!freshKeysByVariantId.has(vId)) freshKeysByVariantId.set(vId, it.key);
      }

      // Link ogni prodotto selezionato al pacco con _package_id. SEQUENZIALE
      // (non Promise.all): Shopify /cart/change.js non gestisce affidabilmente
      // chiamate concorrenti sulla stessa cart.
      // Merge delle properties esistenti: Shopify sostituisce l'intero `properties`
      // anziche' fare merge, quindi devo includere tutte le chiavi pre-esistenti
      // (es. `_engraving_key`) per non perderle.
      const itemPropsByKey = new Map();
      for (const it of cartAfter.items) itemPropsByKey.set(it.key, it.properties || {});
      for (const vId of selectedVariantIds) {
        const freshKey = freshKeysByVariantId.get(vId);
        if (!freshKey) continue; // prodotto rimosso nel frattempo
        const baseProps = itemPropsByKey.get(freshKey) || {};
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: freshKey,
            quantity: 1,
            properties: { ...baseProps, _package_id: pkgId }
          })
        });
      }

      // 2b. Safety net: verifica che almeno 1 prodotto abbia ricevuto il
      // _package_id. Se 0 (change.js tutti falliti, race, prodotto rimosso),
      // rimuoviamo il service line orfano — un pacchetto vuoto non ha senso.
      const verifyRes = await fetch('/cart.js');
      const verifyCart = await verifyRes.json();
      const linkedCount = verifyCart.items.filter((it) =>
        it.properties?._package_id === pkgId && it.handle !== giftHandle
      ).length;
      if (linkedCount === 0) {
        const orphanService = verifyCart.items.find((it) =>
          it.handle === giftHandle && it.properties?._package_id === pkgId
        );
        if (orphanService) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: orphanService.key, quantity: 0 })
          });
        }
        CzToast.show('Pacchetto annullato: nessun articolo è stato collegato.');
        return;
      }

      // 3. Refresh cart drawer: re-fetch section + replace contents.
      // Pattern uguale a Dawn CartItems.onCartUpdate (cart.js:90+).
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        const sectionRes = await fetch('/cart?section_id=cart-drawer');
        const sectionHtml = await sectionRes.text();
        const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const newCartDrawerInner = parsed.querySelector('#CartDrawer');
        const existingCartDrawerInner = cartDrawer.querySelector('#CartDrawer');
        if (newCartDrawerInner && existingCartDrawerInner) {
          existingCartDrawerInner.innerHTML = newCartDrawerInner.innerHTML;
        }
        // Sync is-empty sull'outer (cart non più vuoto)
        cartDrawer.classList.remove('is-empty');
      }

      // 4. Notifica refresh per altri listener (package manager, ecc.)
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'cart-upsell-create' } }));
      document.dispatchEvent(new CustomEvent('cz:cart:updated', { detail: { source: 'cart-upsell-create' } }));
    } catch (err) {
      console.error('[gift-drawer] cart-upsell create failed:', err);
      CzToast.show(`Errore creazione pacchetto: ${err.message}`);
    }
  });

  // Handler confirm per source='cart-edit': aggiorna properties del line item servizio
  document.addEventListener('cz:gift-drawer:confirm', async (e) => {
    if (e.detail.source !== 'cart-edit') return;
    const { serviceLineKey } = e.detail.origin;
    const { photoUrl, photoPublicId, dedication } = e.detail.config;
    await CzPackageOps.refresh();
    const item = CzPackageOps.cart.items.find((it) => it.key === serviceLineKey);
    if (!item) return;
    const newProps = { ...item.properties, Dedica: dedication };
    if (photoUrl) {
      newProps['Foto polaroid'] = photoUrl;
      newProps._photo_public_id = photoPublicId;
    } else {
      delete newProps['Foto polaroid'];
      delete newProps._photo_public_id;
    }
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: serviceLineKey, properties: newProps })
    });
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'package-manager' } }));
    document.dispatchEvent(new CustomEvent('cz:cart:updated', { detail: { source: 'cart-edit-confirm' } }));
  });

  // Helper riusabile: refresh ottimistico del cart drawer.
  // Re-fetch sezione 'cart-drawer' e sostituisce il contenuto di #CartDrawer
  // senza toccare l'outer <cart-drawer> (preserva open/close del dialog).
  async function _refreshCartDrawerSection() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;
    try {
      const res = await fetch('/cart?sections=cart-drawer,cart-icon-bubble');
      const sezioni = await res.json();
      const parsed = new DOMParser().parseFromString(sezioni['cart-drawer'] || '', 'text/html');
      const newInner = parsed.querySelector('#CartDrawer');
      const existingInner = cartDrawer.querySelector('#CartDrawer');
      if (newInner && existingInner) {
        existingInner.innerHTML = newInner.innerHTML;
      }
      // Bolla del contatore nell'header: senza questo restava al numero
      // vecchio dopo le mutazioni cz (aggiunte con servizi, purge orfani)
      // finche' non si ricaricava la pagina.
      const bolla = document.getElementById('cart-icon-bubble');
      if (bolla && sezioni['cart-icon-bubble']) {
        const parsedBolla = new DOMParser().parseFromString(sezioni['cart-icon-bubble'], 'text/html');
        const nuovaBolla = parsedBolla.querySelector('.shopify-section') || parsedBolla.body.firstElementChild;
        if (nuovaBolla) bolla.innerHTML = nuovaBolla.innerHTML;
      }
      // La classe is-empty vive sull'OUTER <cart-drawer>, fuori dal contenuto
      // sostituito: senza questo sync, aggiungendo da carrello vuoto la lista
      // restava nascosta (contatore giusto, righe invisibili) e svuotando il
      // carrello il footer con checkout restava visibile sotto il messaggio.
      const newOuter = parsed.querySelector('cart-drawer');
      if (newOuter) {
        cartDrawer.classList.toggle('is-empty', newOuter.classList.contains('is-empty'));
      }
    } catch (err) {
      console.warn('[cz-cart] refresh cart-drawer section failed:', err);
    }
  }

  // Refresh live cross-drawer: ogni mutazione del cart (cz:cart:updated)
  // sincronizza cart drawer + drawer Pacchi (quest'ultimo ha il proprio listener).
  // Skip: source da Dawn che gia' renderizza la sezione (anti-loop e anti-overhead).
  const _CZ_CART_REFRESH_SKIP = new Set([
    'cart-drawer-section-render',
    'main-cart-section-render',
    'cart-quantity-update',
  ]);
  document.addEventListener('cz:cart:updated', (e) => {
    const source = e.detail?.source;
    if (_CZ_CART_REFRESH_SKIP.has(source)) return;
    _refreshCartDrawerSection();
  });

  // Bottoni inline "Modifica foto/dedica" + "Elimina pacco" sulle card pacco
  // del cart drawer (snippet cz-cart-pacco-card.liquid). Stessa UX del drawer
  // Pacchi regalo; delegation perche' il snippet viene re-renderato da Dawn.
  function _findGiftHandle() {
    return document.querySelector('cz-cart-package-drawer')?.dataset.giftHandle
        || document.querySelector('cz-cart-package-manager')?.dataset.giftHandle
        || '';
  }

  async function _editCartPacco(pkgId) {
    await CzPackageOps.refresh();
    const snap = CzPackageOps.snapshot();
    const pkg = snap.packages.find((p) => p.id === pkgId);
    if (!pkg) return;
    document.dispatchEvent(new CustomEvent('cz:gift-drawer:open', {
      detail: {
        source: 'cart-edit',
        packageId: pkgId,
        serviceLineKey: pkg.serviceLineKey,
        currentConfig: {
          photoUrl: pkg.photoUrl,
          photoPublicId: pkg.photoPublicId,
          dedication: pkg.dedication,
        },
      },
    }));
  }

  async function _deleteCartPacco(pkgId) {
    await CzPackageOps.refresh();
    const cart = CzPackageOps.cart;
    const giftHandle = _findGiftHandle();
    const serviceLine = cart.items.find((it) =>
      it.properties?._package_id === pkgId && it.handle === giftHandle
    );
    if (!serviceLine) return;
    const ok = await (window.CzConfirm ? window.CzConfirm.show({
      title: 'Elimini il pacco?',
      body: 'Gli articoli all\'interno torneranno sciolti nel carrello. Il pacchetto in omaggio sarà rimosso.',
      confirmText: 'Elimina',
      cancelText: 'Annulla',
    }) : Promise.resolve(true));
    if (!ok) return;
    const itemsInPkg = cart.items.filter((it) =>
      it.properties?._package_id === pkgId && it.handle !== giftHandle
    );
    // Setta `_package_id: ''` invece di eliminare la chiave: `/cart/change.js`
    // fa merge delle properties, quindi un `delete` lato JS non rimuove la
    // property a livello cart → l'articolo restava skippato dal template
    // (cart-drawer.liquid:87 skippa item con `_package_id != blank`) e
    // sembrava sparito dal carrello. Stringa vuota = blank in Liquid.
    for (const it of itemsInPkg) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: it.key,
          quantity: it.quantity,
          properties: { ...(it.properties || {}), _package_id: '' },
        }),
      });
    }
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: serviceLine.key, quantity: 0 }),
    });
    document.dispatchEvent(new CustomEvent('cz:cart:updated', { detail: { source: 'cart-pacco-delete' } }));
  }

  document.addEventListener('click', (e) => {
    const editBtn = e.target.closest('[data-cz-pacco-edit]');
    if (editBtn) {
      e.preventDefault();
      _editCartPacco(editBtn.dataset.czPaccoEdit);
      return;
    }
    const delBtn = e.target.closest('[data-cz-pacco-delete]');
    if (delBtn) {
      e.preventDefault();
      _deleteCartPacco(delBtn.dataset.czPaccoDelete);
      return;
    }
    const removePackagedItemBtn = e.target.closest('[data-cz-pacco-item-remove]');
    if (removePackagedItemBtn) {
      e.preventDefault();
      _removePackagedItem(
        removePackagedItemBtn.dataset.czPaccoItemRemove,
        removePackagedItemBtn.dataset.packageId,
      );
    }
  });

  // Click "Rimuovi" su un item dentro un pacco → modale a 2 opzioni.
  // "Solo dal pacchetto" → libera (cancella _package_id, item torna loose).
  // "Dal carrello" → elimina del tutto (quantity 0).
  async function _removePackagedItem(lineKey, pkgId) {
    await CzPackageOps.refresh();
    const item = CzPackageOps.cart.items.find((it) => it.key === lineKey);
    if (!item) return;
    const choice = await CzConfirm.choose({
      eyebrow: 'Rimuovi articolo',
      title: 'Cosa vuoi fare?',
      body: 'Puoi liberare l\'articolo dal pacchetto regalo (resta nel carrello) oppure eliminarlo del tutto.',
      choices: [
        { value: 'package', label: 'Solo dal pacchetto', variant: 'secondary' },
        { value: 'cart', label: 'Elimina dal carrello', variant: 'primary' },
      ],
      cancelText: 'Annulla',
    });
    if (!choice) return;
    if (choice === 'package') {
      // Libera: merge properties + _package_id vuoto.
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: lineKey,
          quantity: item.quantity,
          properties: { ...(item.properties || {}), _package_id: '' },
        }),
      });
    } else if (choice === 'cart') {
      // Elimina del tutto (incluso eventuale engraving service line collegato).
      const engKey = item.properties?._engraving_key;
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0 }),
      });
      if (engKey) {
        const engService = CzPackageOps.cart.items.find((it) => it.properties?._for_engraving_key === engKey);
        if (engService) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: engService.key, quantity: 0 }),
          });
        }
      }
    }
    document.dispatchEvent(new CustomEvent('cz:cart:updated', { detail: { source: 'cart-pacco-item-remove' } }));
  }

  // ============================================================
  // Cart engraving card — inline Modifica / Elimina / toggle dettagli.
  // Delegation perche' lo snippet cz-cart-item viene re-renderato da Dawn.
  // ============================================================
  const _CZ_ENG_FONT_KEY = { 'Script': 'script', 'Serif': 'serif', 'Block': 'sans' };
  const _CZ_ENG_FONT_LABEL = { script: 'Script', serif: 'Serif', sans: 'Block' };
  const _CZ_ENG_METAL_KEY = {
    'Oro giallo': 'oro_giallo',
    'Oro bianco': 'oro_bianco',
    'Oro rosa': 'oro_rosa',
    'Argento': 'argento',
    'Platino': 'platino',
    'Altri materiali': 'altri_materiali',
  };
  const _CZ_ENG_METAL_LABEL = {
    oro_giallo: 'Oro giallo',
    oro_bianco: 'Oro bianco',
    oro_rosa: 'Oro rosa',
    argento: 'Argento',
    platino: 'Platino',
    altri_materiali: 'Altri materiali',
  };

  async function _editCartEngraving(engravingKey) {
    await CzPackageOps.refresh();
    const cart = CzPackageOps.cart;
    const parent = cart.items.find((it) => it.properties?._engraving_key === engravingKey);
    const service = cart.items.find((it) => it.properties?._for_engraving_key === engravingKey);
    if (!parent || !service) return;
    const text = service.properties?.['Testo da incidere'] || '';
    const font = _CZ_ENG_FONT_KEY[service.properties?.['Stile']] || 'script';
    const metalColor = _CZ_ENG_METAL_KEY[service.properties?.['_finitura'] || service.properties?.['Finitura']] || 'argento';
    document.dispatchEvent(new CustomEvent('cz:engraving-drawer:open', {
      detail: {
        source: 'cart-edit',
        engravingKey,
        parentLineKey: parent.key,
        serviceLineKey: service.key,
        currentConfig: { active: true, text, font, metalColor },
      },
    }));
  }

  async function _deleteCartEngraving(engravingKey) {
    await CzPackageOps.refresh();
    const cart = CzPackageOps.cart;
    const parent = cart.items.find((it) => it.properties?._engraving_key === engravingKey);
    const service = cart.items.find((it) => it.properties?._for_engraving_key === engravingKey);
    if (!parent || !service) return;
    const ok = await (window.CzConfirm ? window.CzConfirm.show({
      title: 'Elimini l\'incisione?',
      body: 'Il prodotto rimane nel carrello, la personalizzazione viene rimossa.',
      confirmText: 'Elimina',
      cancelText: 'Annulla',
    }) : Promise.resolve(true));
    if (!ok) return;
    // Pulisci `_engraving_key` dal parent (merge — Shopify non rimuove la chiave,
    // ma stringa vuota = blank nei filtri Liquid e fa sparire la card incisione).
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: parent.key,
        quantity: parent.quantity,
        properties: { ...(parent.properties || {}), _engraving_key: '' },
      }),
    });
    // Elimina service line incisione.
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: service.key, quantity: 0 }),
    });
    document.dispatchEvent(new CustomEvent('cz:cart:updated', { detail: { source: 'cart-engraving-delete' } }));
  }

  // Handler confirm per source='cart-edit' engraving: aggiorna properties del service line.
  document.addEventListener('cz:engraving-drawer:confirm', async (e) => {
    if (e.detail.source !== 'cart-edit') return;
    const { serviceLineKey } = e.detail.origin;
    const { text, font, metalColor } = e.detail.config;
    await CzPackageOps.refresh();
    const item = CzPackageOps.cart.items.find((it) => it.key === serviceLineKey);
    if (!item) return;
    const newProps = {
      ...item.properties,
      'Testo da incidere': text,
      'Stile': _CZ_ENG_FONT_LABEL[font] || 'Script',
      '_finitura': _CZ_ENG_METAL_LABEL[metalColor] || 'Argento',
    };
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: serviceLineKey, quantity: item.quantity, properties: newProps }),
    });
    document.dispatchEvent(new CustomEvent('cz:cart:updated', { detail: { source: 'cart-engraving-edit-confirm' } }));
  });

  document.addEventListener('click', (e) => {
    // Toggle "Vedi dettagli ↔ Nascondi dettagli"
    const toggleBtn = e.target.closest('[data-engraving-toggle]');
    if (toggleBtn) {
      const card = toggleBtn.closest('.cz-cart-engraving');
      if (card) {
        const isOpen = card.dataset.open === 'true';
        card.dataset.open = isOpen ? 'false' : 'true';
        toggleBtn.setAttribute('aria-expanded', String(!isOpen));
      }
      return;
    }
    const editBtn = e.target.closest('[data-cz-engraving-edit]');
    if (editBtn) {
      e.preventDefault();
      _editCartEngraving(editBtn.dataset.czEngravingEdit);
      return;
    }
    const delBtn = e.target.closest('[data-cz-engraving-delete]');
    if (delBtn) {
      e.preventDefault();
      _deleteCartEngraving(delBtn.dataset.czEngravingDelete);
    }
  });

  // Purge orfani al load di /cart e dopo ogni cart:updated (con guard anti-loop)
  document.addEventListener('DOMContentLoaded', async () => {
    if (window.location.pathname === '/cart' || document.querySelector('cz-cart-package-manager')) {
      await CzPackageOps.refresh();
      await CzPackageOps.purgeOrphans();
    }
  });

  document.addEventListener('cart:updated', async (e) => {
    if (e.detail?.source === 'cz-purge' || e.detail?.source === 'package-manager') return;
    await CzPackageOps.purgeOrphans();
  });

  // I bottoni Rimuovi/quantità di Dawn NON emettono 'cart:updated': parlano
  // solo col pub-sub interno (publish cartUpdate). Senza questo hook, togliere
  // il prodotto dal drawer lasciava i servizi collegati orfani — carrello
  // fantasma "1 prodotto · €8,90" con lista vuota — fino al reload.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
      if (event?.source === 'cz-addons') return;
      const rimossi = await CzPackageOps.purgeOrphans();
      if (rimossi > 0) {
        await _refreshCartDrawerSection();
        document.dispatchEvent(new CustomEvent('cz:cart:updated', { detail: { source: 'cz-purge' } }));
      }
    });
  }

  // (Vecchio native confirm() rimosso: l'UX ora vive in CzCartPackageDrawer.handleRemove
  // dove sappiamo se stiamo davvero rimuovendo l'ultimo articolo + apriamo CzConfirm
  // modal stilizzato Cozzari invece di confirm() del browser.)

  // Quick-action "Incarta" sull'item card del cart drawer.
  // Click su [data-cz-incarta] → apre wizard cart-upsell con preSelectedKey
  // → salta lo step picker → step 2 foto+dedica → conferma crea pacco nuovo
  // con questo solo articolo dentro.
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-cz-incarta]');
    if (!btn) return;
    e.preventDefault();
    const lineKey = btn.dataset.lineKey;
    if (!lineKey) return;
    document.dispatchEvent(new CustomEvent('cz:gift-drawer:open', {
      detail: { source: 'cart-upsell', preSelectedKey: lineKey }
    }));
  });

  const CzToast = {
    show(message, type = 'error') {
      const t = document.createElement('div');
      t.className = `cz-toast cz-toast--${type}`;
      t.textContent = message;
      document.body.appendChild(t);
      requestAnimationFrame(() => t.classList.add('is-visible'));
      setTimeout(() => {
        t.classList.remove('is-visible');
        setTimeout(() => t.remove(), 320);
      }, 4000);
    }
  };
  window.CzToast = CzToast;

  // CzConfirm — modal di conferma Promise-based stile Cozzari.
  // Sostituisce native confirm() (brutto) con un dialog HTML coerente
  // con Celebra l'amore (Cormorant + gold accents + white card).
  // Uso: const ok = await CzConfirm.show({ eyebrow, title, body, confirmText, cancelText });
  const CzConfirm = {
    async show({ eyebrow = 'Conferma', title = 'Sei sicuro?', body = '', confirmText = 'Conferma', cancelText = 'Annulla' } = {}) {
      return new Promise((resolve) => {
        const dialog = document.createElement('dialog');
        dialog.className = 'cz-confirm';
        dialog.innerHTML = `
          <div class="cz-confirm__card">
            <p class="cz-confirm__eyebrow">${this._escapeHTML(eyebrow)}</p>
            <h2 class="cz-confirm__title">${title}</h2>
            ${body ? `<p class="cz-confirm__body">${body}</p>` : ''}
            <div class="cz-confirm__actions">
              <button type="button" data-cancel class="cz-confirm__btn cz-confirm__btn--secondary">${this._escapeHTML(cancelText)}</button>
              <button type="button" data-confirm class="cz-confirm__btn cz-confirm__btn--primary">${this._escapeHTML(confirmText)}</button>
            </div>
          </div>
        `;
        document.body.appendChild(dialog);
        dialog.showModal();
        const cleanup = (result) => {
          try { dialog.close(); } catch (_) {}
          dialog.remove();
          resolve(result);
        };
        dialog.querySelector('[data-cancel]').addEventListener('click', () => cleanup(false));
        dialog.querySelector('[data-confirm]').addEventListener('click', () => cleanup(true));
        dialog.addEventListener('cancel', (e) => { e.preventDefault(); cleanup(false); });
        dialog.addEventListener('click', (e) => { if (e.target === dialog) cleanup(false); });
      });
    },
    _escapeHTML(str) {
      return String(str || '').replace(/[&<>"']/g, m => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
      }[m]));
    },

    // Variante multi-opzione: ritorna il `value` del bottone scelto (o null se cancel).
    // Uso: const ret = await CzConfirm.choose({ eyebrow, title, body, choices: [...], cancelText });
    // choices: Array<{ value: string, label: string, variant?: 'primary'|'secondary' }>
    async choose({ eyebrow = 'Conferma', title = 'Cosa vuoi fare?', body = '', choices = [], cancelText = 'Annulla' } = {}) {
      return new Promise((resolve) => {
        const dialog = document.createElement('dialog');
        dialog.className = 'cz-confirm';
        const choicesHTML = choices.map((c) =>
          `<button type="button" data-choice="${this._escapeHTML(c.value)}" class="cz-confirm__btn cz-confirm__btn--${c.variant === 'primary' ? 'primary' : 'secondary'}">${this._escapeHTML(c.label)}</button>`
        ).join('');
        dialog.innerHTML = `
          <div class="cz-confirm__card">
            <p class="cz-confirm__eyebrow">${this._escapeHTML(eyebrow)}</p>
            <h2 class="cz-confirm__title">${title}</h2>
            ${body ? `<p class="cz-confirm__body">${body}</p>` : ''}
            <div class="cz-confirm__actions cz-confirm__actions--stacked">
              ${choicesHTML}
              <button type="button" data-cancel class="cz-confirm__btn cz-confirm__btn--ghost">${this._escapeHTML(cancelText)}</button>
            </div>
          </div>
        `;
        document.body.appendChild(dialog);
        dialog.showModal();
        const cleanup = (result) => {
          try { dialog.close(); } catch (_) {}
          dialog.remove();
          resolve(result);
        };
        dialog.querySelectorAll('[data-choice]').forEach((btn) => {
          btn.addEventListener('click', () => cleanup(btn.dataset.choice));
        });
        dialog.querySelector('[data-cancel]').addEventListener('click', () => cleanup(null));
        dialog.addEventListener('cancel', (e) => { e.preventDefault(); cleanup(null); });
        dialog.addEventListener('click', (e) => { if (e.target === dialog) cleanup(null); });
      });
    },
  };
  window.CzConfirm = CzConfirm;

  // <cz-cart-package-drawer> — drawer tavolo da incarto
  class CzCartPackageDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-drawer]');
      this.body = this.querySelector('[data-body]');
      this.closeBtn = this.querySelector('[data-close]');

      this.closeBtn?.addEventListener('click', () => this.close());
      this.dialog?.addEventListener('click', (e) => {
        if (e.target === this.dialog) this.close();
      });

      document.addEventListener('cz:package-drawer:open', () => this.open());
      document.addEventListener('cz:cart:updated', () => {
        if (this.dialog?.open) this.render();
      });
    }

    open() {
      this.render();
      // iOS: il <dialog> nativo non blocca lo scroll della pagina dietro —
      // tastiera e scroll interno la trascinano giu'. Si ricorda la posizione
      // qui e la si ripristina alla chiusura (listener 'close', una volta sola).
      this._scrollPrima = window.scrollY;
      if (!this._scrollWired) {
        this._scrollWired = true;
        this.dialog.addEventListener('close', () => {
          if (typeof this._scrollPrima === 'number') window.scrollTo(0, this._scrollPrima);
        });
      }
      this.dialog.showModal();
    }

    close() {
      this.dialog.close();
    }

    async render() {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      const giftHandle = this.dataset.giftHandle;

      const packages = new Map();
      const loose = [];

      cart.items.forEach(item => {
        const pkgId = item.properties?._package_id;
        if (pkgId) {
          if (!packages.has(pkgId)) packages.set(pkgId, { id: pkgId, items: [], service: null });
          // Il service line (gift wrap product) appartiene al pacco MA non va
          // mostrato come articolo regolare: si traccia separatamente per foto/dedica.
          if (item.handle === giftHandle) {
            packages.get(pkgId).service = item;
          } else if (!item.properties?._for_engraving_key) {
            packages.get(pkgId).items.push(item);
          }
        } else {
          if (item.handle !== giftHandle && item.handle !== 'incisione-personalizzata') {
            loose.push(item);
          }
        }
      });

      this.body.innerHTML = this.bodyHTML(Array.from(packages.values()), loose);
      this.attachListeners(Array.from(packages.values()), loose);
    }

    bodyHTML(packages, loose) {
      const pkgCount = packages.length;
      const looseCount = loose.length;

      let html = '<div class="cz-pkg-drop-trash" data-drop="trash">Togli dal pacco</div>';

      if (pkgCount > 0) {
        html += `<p class="cz-pkg-section-label">Pacchi attuali (${pkgCount})</p>`;
        packages.forEach((pkg, i) => {
          html += this.packageCardHTML(pkg, i + 1);
        });
      }

      // Drop zone "Nuovo pacco" sempre presente nel markup (CSS la nasconde
      // se data-dragging non e' settato sul drawer).
      html += '<div class="cz-pkg-drop-new" data-drop="new">Nuovo pacco regalo</div>';

      if (looseCount > 0) {
        html += `<p class="cz-pkg-section-label">Articoli senza pacco (${looseCount})</p>`;
        html += `<ul class="cz-pkg-loose-list">`;
        loose.forEach(item => { html += this.looseItemHTML(item); });
        html += `</ul>`;
      }

      if (pkgCount === 0 && looseCount === 0) {
        html = `<p style="text-align:center;color:#888;padding:40px 0;">Nessun articolo da raggruppare.</p>`;
      }

      return html;
    }

    _fmtPrice(cents) {
      // Format coerente con Liquid: "€ 1.234" / "€ 350" (no .00, prefisso €<spazio>)
      if (cents == null) return '';
      const euros = cents / 100;
      const hasCents = euros !== Math.floor(euros);
      const formatted = hasCents
        ? euros.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : euros.toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
      return `€ ${formatted}`;
    }

    packageCardHTML(pkg, index) {
      const svc = pkg.service;
      const polaroidUrl = svc?.properties?.['Foto polaroid'] || '';
      const dedica = (svc?.properties?.['Dedica'] || '').trim();

      // Polaroid o placeholder (stesso pattern del cart drawer cz-cart-pacco-card.liquid)
      const polaroidInner = polaroidUrl
        ? `<img class="cz-pkg-card__polaroid-img" src="${this.escapeHTML(polaroidUrl)}" alt="" loading="lazy">`
        : `<div class="cz-pkg-card__polaroid-img cz-pkg-card__polaroid-img--placeholder">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.1" aria-hidden="true">
              <rect x="3" y="6" width="18" height="14" rx="1"/>
              <circle cx="12" cy="13" r="4"/>
              <path d="M8 6 L9.5 4 H14.5 L16 6"/>
            </svg>
          </div>`;
      const polaroidHTML = `<div class="cz-pkg-card__polaroid" aria-hidden="true">${polaroidInner}</div>`;

      // Pricing badge: gratis (in omaggio) o + €X
      let subHTML = '';
      if (svc) {
        const finalLine = svc.final_line_price;
        const originalLine = svc.original_line_price;
        if (finalLine === 0 && originalLine > 0) {
          subHTML = `<span class="cz-pkg-card__sub">
            <s>${this._fmtPrice(originalLine)}</s>
            <span class="cz-pkg-card__sub-tag">in omaggio</span>
          </span>`;
        } else if (finalLine > 0) {
          subHTML = `<span class="cz-pkg-card__sub">+ ${this._fmtPrice(finalLine)}</span>`;
        }
      }

      // Items list
      const itemsHTML = pkg.items.map(item => `
        <li class="cz-pkg-card__item" data-line-item-key="${item.key}" data-source="pkg" data-package-id="${pkg.id}">
          <img class="cz-pkg-card__item-thumb" src="${this.escapeHTML(item.image)}" alt="">
          <div class="cz-pkg-card__item-meta">
            <p class="cz-pkg-card__item-brand">${this.escapeHTML((item.vendor || '').toLowerCase())}</p>
            <p class="cz-pkg-card__item-title">${this.escapeHTML(item.product_title)}</p>
          </div>
          <span class="cz-pkg-card__item-grip" aria-label="trascina"></span>
        </li>
      `).join('');

      return `
        <article class="cz-pkg-card" data-package-id="${pkg.id}">
          <div class="cz-pkg-card__hd">
            ${polaroidHTML}
            <div class="cz-pkg-card__hd-meta">
              <h3 class="cz-pkg-card__name">Pacco ${index}</h3>
              ${dedica ? `<p class="cz-pkg-card__dedica">${this.escapeHTML(dedica)}</p>` : ''}
              ${subHTML}
            </div>
          </div>
          <ul class="cz-pkg-card__items">${itemsHTML}</ul>
          <div class="cz-pkg-card__ft">
            <button type="button" class="cz-pkg-card__edit" data-edit data-package-id="${pkg.id}">Modifica foto/dedica</button>
            <button type="button" class="cz-pkg-card__del" data-delete-pkg data-package-id="${pkg.id}">Elimina pacco</button>
          </div>
        </article>
      `;
    }

    looseItemHTML(item) {
      return `
        <li class="cz-pkg-loose-item" data-line-item-key="${item.key}" data-source="loose">
          <img class="cz-pkg-loose-item__thumb" src="${this.escapeHTML(item.image)}" alt="">
          <div class="cz-pkg-loose-item__info">
            <p class="cz-pkg-loose-item__brand">${this.escapeHTML(item.vendor || '')}</p>
            <p class="cz-pkg-loose-item__title">${this.escapeHTML(item.product_title)}</p>
          </div>
          <span class="cz-pkg-loose-item__grip" aria-label="trascina"></span>
        </li>
      `;
    }

    attachListeners(packages, loose) {
      this.body.querySelectorAll('[data-remove]').forEach(btn => {
        btn.addEventListener('click', () => this.handleRemove(btn.dataset.lineKey));
      });
      this.body.querySelectorAll('[data-edit]').forEach(btn => {
        btn.addEventListener('click', () => this.handleEdit(btn.dataset.packageId));
      });
      this.body.querySelectorAll('[data-delete-pkg]').forEach(btn => {
        btn.addEventListener('click', () => this._handleDeletePackage(btn.dataset.packageId));
      });
      this.body.querySelector('[data-create-package]')?.addEventListener('click', () => this.handleCreateEmpty());

      // Pointer Events: long-press (350ms, <8px movimento) avvia drag,
      // tap singolo apre bottom sheet (gestito in Task 12).
      this._attachPointerEvents(packages, loose);
    }

    _attachPointerEvents(packages, loose) {
      this._packages = packages;
      this._loose = loose;
      const items = this.body.querySelectorAll('.cz-pkg-card__item, .cz-pkg-loose-item');
      items.forEach(item => {
        item.addEventListener('pointerdown', (e) => this._onPointerDown(e, item));
      });
    }

    _onPointerDown(e, item) {
      if (e.button !== undefined && e.button !== 0) return; // solo tasto sx
      // Guardia contro pointerdown rapidi successivi (multi-touch o re-render
      // durante drag): pulisci lo state precedente per evitare listener leak.
      if (this._pointerState) {
        clearTimeout(this._pointerState.longPressTimer);
        this._cleanupPointerState(this._pointerState.item);
      }
      this._pointerState = {
        id: e.pointerId,
        item,
        startX: e.clientX,
        startY: e.clientY,
        moved: false,
        dragActive: false,
        longPressTimer: null,
      };

      this._pointerState.longPressTimer = setTimeout(() => {
        if (!this._pointerState || this._pointerState.moved) return;
        this._startDrag(e, item);
      }, 350);

      this._boundMove = (ev) => this._onPointerMove(ev);
      this._boundUp = (ev) => this._onPointerUp(ev);
      this._boundCancel = (ev) => this._onPointerCancel(ev);
      item.addEventListener('pointermove', this._boundMove);
      item.addEventListener('pointerup', this._boundUp);
      item.addEventListener('pointercancel', this._boundCancel);
    }

    _onPointerMove(e) {
      if (!this._pointerState) return;
      const dx = e.clientX - this._pointerState.startX;
      const dy = e.clientY - this._pointerState.startY;
      if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
        this._pointerState.moved = true;
        if (!this._pointerState.dragActive) {
          clearTimeout(this._pointerState.longPressTimer);
        }
      }
      if (this._pointerState.dragActive) {
        this._updateGhost(e.clientX, e.clientY);
        this._updateDropTarget(e.clientX, e.clientY);
      }
    }

    _onPointerUp(e) {
      if (!this._pointerState) return;
      const { item, dragActive, moved, longPressTimer } = this._pointerState;
      clearTimeout(longPressTimer);

      if (dragActive) {
        this._endDrag(e);
      } else if (!moved) {
        // Tap singolo: apre bottom sheet (Task 12).
        if (this._openActionSheet) this._openActionSheet(item);
      }

      this._cleanupPointerState(item);
    }

    _onPointerCancel(e) {
      if (!this._pointerState) return;
      clearTimeout(this._pointerState.longPressTimer);
      if (this._pointerState.dragActive) {
        this._endDrag(e, true);
      }
      this._cleanupPointerState(this._pointerState.item);
    }

    _cleanupPointerState(item) {
      if (item && this._boundMove) item.removeEventListener('pointermove', this._boundMove);
      if (item && this._boundUp) item.removeEventListener('pointerup', this._boundUp);
      if (item && this._boundCancel) item.removeEventListener('pointercancel', this._boundCancel);
      this._pointerState = null;
    }

    _startDrag(e, item) {
      this._pointerState.dragActive = true;
      item.classList.add('is-dragged');
      this._ghost = this._createGhost(item);
      this._updateGhost(e.clientX, e.clientY);

      // Capture pointer per ricevere pointermove/up anche se il dito esce dall'item.
      try { item.setPointerCapture(e.pointerId); } catch (_) {}

      // Imposta data-dragging sul drawer per svelare drop zones.
      const source = item.dataset.source; // 'pkg' o 'loose'
      this.dialog?.setAttribute('data-dragging', source === 'pkg' ? 'pkg' : 'any');

      // Disabilita scroll del body mentre drag (mobile).
      document.body.style.overscrollBehavior = 'contain';
    }

    _updateGhost(x, y) {
      if (!this._ghost) return;
      this._ghost.style.left = `${x + 12}px`;
      this._ghost.style.top = `${y - 24}px`;
    }

    _updateDropTarget(x, y) {
      // Rimuovi highlight precedente.
      if (this._currentDropTarget) {
        this._currentDropTarget.classList.remove('is-drop-target');
        this._currentDropTarget = null;
      }

      const target = this._findDropTarget(x, y);
      if (target) {
        target.classList.add('is-drop-target');
        this._currentDropTarget = target;
      }
    }

    _findDropTarget(x, y) {
      // Nasconde temporaneamente il ghost per non interferire con elementFromPoint.
      const ghostDisplay = this._ghost ? this._ghost.style.display : null;
      if (this._ghost) this._ghost.style.display = 'none';

      const el = document.elementFromPoint(x, y);

      if (this._ghost) this._ghost.style.display = ghostDisplay || '';

      if (!el) return null;

      // Walk up cercando: cz-pkg-card, cz-pkg-drop-new, cz-pkg-drop-trash.
      // NON considera valido il pacco di origine (se l'item viene da li').
      const originPkgId = this._pointerState?.item?.dataset?.packageId;

      let cur = el;
      while (cur && cur !== this.body) {
        if (cur.classList?.contains('cz-pkg-drop-trash')) return cur;
        if (cur.classList?.contains('cz-pkg-drop-new')) return cur;
        if (cur.classList?.contains('cz-pkg-card')) {
          const pkgId = cur.dataset.packageId;
          if (pkgId && pkgId !== originPkgId) return cur;
        }
        cur = cur.parentElement;
      }
      return null;
    }

    async _endDrag(e, cancelled = false) {
      const target = this._currentDropTarget;
      const item = this._pointerState?.item;

      // Cleanup visuale immediato.
      if (item) item.classList.remove('is-dragged');
      this._destroyGhost();
      if (this._currentDropTarget) {
        this._currentDropTarget.classList.remove('is-drop-target');
        this._currentDropTarget = null;
      }
      this.dialog?.removeAttribute('data-dragging');
      document.body.style.overscrollBehavior = '';

      if (cancelled || !target || !item) return;

      await this._commitDrop(item, target);
    }

    async _commitDrop(item, target) {
      const lineKey = item.dataset.lineItemKey;
      if (!lineKey) return;
      const originPkgId = item.dataset.packageId;

      try {
        // CASO 1: drop su altro pacco esistente.
        if (target.classList.contains('cz-pkg-card')) {
          const targetPkgId = target.dataset.packageId;
          if (!targetPkgId) return;
          await this._setPackageId(lineKey, targetPkgId);
          document.dispatchEvent(new CustomEvent('cz:cart:updated', { detail: { source: 'pkg-drawer-v2' } }));
        }
        // CASO 2: drop su "+ Nuovo pacco regalo" → crea pacco + sposta item dentro.
        else if (target.classList.contains('cz-pkg-drop-new')) {
          await this._createPackageAndMove(lineKey);
          document.dispatchEvent(new CustomEvent('cz:cart:updated', { detail: { source: 'pkg-drawer-v2' } }));
        }
        // CASO 3: drop su "x Togli dal pacco" → item esce dal pacco (loose).
        else if (target.classList.contains('cz-pkg-drop-trash')) {
          await this._setPackageId(lineKey, '');
          document.dispatchEvent(new CustomEvent('cz:cart:updated', { detail: { source: 'pkg-drawer-v2' } }));
        }
        else {
          return;
        }

        // Post-commit: se il pacco di origine e' rimasto vuoto, CzConfirm + delete.
        if (originPkgId) {
          const cart = await fetch('/cart.js').then(r => r.json());
          const remaining = cart.items.filter(it =>
            it.properties?._package_id === originPkgId && !it.properties?._service
          );
          if (remaining.length === 0) {
            const serviceLine = cart.items.find(it =>
              it.properties?._package_id === originPkgId && it.properties?._service
            );
            if (serviceLine) {
              await this._deletePackage(originPkgId, serviceLine.key);
            }
          }
        }
      } catch (err) {
        console.error('[cz-pkg] commitDrop failure', err);
        this._showToast('Qualcosa è andato storto, riprova');
        document.dispatchEvent(new CustomEvent('cz:cart:updated', { detail: { source: 'pkg-drawer-v2-error-rerender' } }));
      }
    }

    async _createPackageAndMove(lineKey) {
      const giftHandle = this.dataset.giftHandle;
      if (!giftHandle) {
        console.error('[cz-pkg] giftHandle mancante in data-gift-handle');
        return;
      }

      // 1. Recupera il variant_id del gift product via /products/{handle}.js
      const product = await fetch(`/products/${giftHandle}.js`).then(r => r.json());
      const variantId = product.variants?.[0]?.id;
      if (!variantId) {
        throw new Error('[cz-pkg] variantId non trovato per gift product');
      }

      // 2. Crea service line con un _package_id univoco.
      const newPkgId = `pkg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const addResp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1,
            properties: {
              _package_id: newPkgId,
              _service: 'true',
            },
          }],
        }),
      }).then(r => r.json());

      // 3. Sposta l'item nel nuovo pacco.
      await this._setPackageId(lineKey, newPkgId);
    }

    async _deletePackage(pkgId, serviceLineKey) {
      // Conferma utente (riusa CzConfirm gia' esistente nella codebase).
      const ok = await (window.CzConfirm ? window.CzConfirm.show({
        title: 'Pacco rimasto vuoto',
        body: 'L\'ultimo articolo del pacco e\' stato rimosso. Eliminiamo anche il pacchetto?',
        confirmText: 'Elimina pacco',
        cancelText: 'Tieni vuoto',
      }) : Promise.resolve(true));
      if (!ok) return;

      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: serviceLineKey, quantity: 0 }),
      });
      document.dispatchEvent(new CustomEvent('cz:cart:updated', { detail: { source: 'pkg-drawer-v2-delete' } }));
    }

    async _handleDeletePackage(pkgId) {
      if (!pkgId) return;
      const cart = await fetch('/cart.js').then(r => r.json());
      const giftHandle = this.dataset.giftHandle;
      // Identifica il service line via handle (NON via `_service`: quella property
      // viene settata solo in _createPackageAndMove. I service line creati da PDP,
      // cart-upsell, createEmptyPackage NON hanno `_service` → filtro falliva
      // silenziosamente e l'azione "Elimina pacco" non faceva nulla).
      const serviceLine = cart.items.find(it =>
        it.properties?._package_id === pkgId && it.handle === giftHandle
      );
      if (!serviceLine) return;

      const ok = await (window.CzConfirm ? window.CzConfirm.show({
        title: 'Elimini il pacco?',
        body: 'Gli articoli all\'interno torneranno sciolti nel carrello. Il pacchetto in omaggio sara\' rimosso.',
        confirmText: 'Elimina',
        cancelText: 'Annulla',
      }) : Promise.resolve(true));
      if (!ok) return;

      // Libera tutti gli items del pacco.
      const itemsInPkg = cart.items.filter(it =>
        it.properties?._package_id === pkgId && it.handle !== giftHandle
      );
      for (const it of itemsInPkg) {
        await this._setPackageId(it.key, '');
      }
      // Elimina il service line.
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: serviceLine.key, quantity: 0 }),
      });
      document.dispatchEvent(new CustomEvent('cz:cart:updated', { detail: { source: 'pkg-drawer-v2-delete-explicit' } }));
    }

    async _setPackageId(lineKey, packageId) {
      // Shopify /cart/change.js sostituisce l'intero oggetto `properties`
      // anziche' farne merge. Passando solo { _package_id: X } perderei
      // tutte le altre proprieta' dell'item (es. `_engraving_key` → card
      // incisione sparita). Devo preservarle leggendo dal cart corrente.
      const cart = await fetch('/cart.js').then((r) => r.json());
      const item = cart.items.find((it) => it.key === lineKey);
      const baseProps = (item && item.properties) || {};
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: lineKey,
          quantity: item ? item.quantity : 1,
          properties: { ...baseProps, _package_id: packageId },
        }),
      });
    }

    _createGhost(item) {
      const isPkg = item.classList.contains('cz-pkg-card__item');
      const thumbEl = item.querySelector(isPkg ? '.cz-pkg-card__item-thumb' : '.cz-pkg-loose-item__thumb');
      const titleEl = item.querySelector(isPkg ? '.cz-pkg-card__item-title' : '.cz-pkg-loose-item__title');

      const ghost = document.createElement('div');
      ghost.className = 'cz-pkg-ghost';
      ghost.innerHTML = `
        <img class="cz-pkg-ghost__thumb" src="${thumbEl?.src || ''}" alt="">
        <span class="cz-pkg-ghost__title">${titleEl?.textContent || ''}</span>
      `;
      document.body.appendChild(ghost);
      return ghost;
    }

    _destroyGhost() {
      if (this._ghost) {
        this._ghost.remove();
        this._ghost = null;
      }
    }

    _showToast(message) {
      const toast = document.createElement('div');
      toast.style.cssText = `
        position: absolute;
        bottom: 24px;
        left: 50%;
        transform: translateX(-50%);
        background: rgba(26, 26, 23, 0.92);
        color: #fff;
        padding: 10px 18px;
        font-family: var(--cz-font-cormorant, 'Cormorant Garamond', serif);
        font-style: italic;
        font-size: 13px;
        z-index: 100;
        box-shadow: 0 6px 20px rgba(0,0,0,0.18);
        animation: cz-pkg-fade-in 200ms ease;
      `;
      toast.textContent = message;
      this.dialog?.appendChild(toast);
      setTimeout(() => toast.remove(), 3000);
    }

    _openActionSheet(item) {
      this._closeActionSheet();

      const source = item.dataset.source; // 'pkg' o 'loose'
      const lineKey = item.dataset.lineItemKey;
      const originPkgId = item.dataset.packageId || null;

      const sheetWrap = document.createElement('div');
      sheetWrap.className = 'cz-pkg-sheet-wrap';
      sheetWrap.innerHTML = this._buildSheetHTML(item, source, originPkgId);
      this.dialog?.appendChild(sheetWrap);

      this._activeSheet = sheetWrap;
      this._activeSheetItem = { lineKey, source, originPkgId };

      // Wire azioni.
      sheetWrap.querySelectorAll('[data-sheet-action]').forEach(btn => {
        btn.addEventListener('click', () => this._handleSheetAction(btn.dataset.sheetAction, btn.dataset.target));
      });
      sheetWrap.querySelector('[data-sheet-cancel]')?.addEventListener('click', () => this._closeActionSheet());
      sheetWrap.querySelector('.cz-pkg-sheet-back')?.addEventListener('click', () => this._closeActionSheet());
    }

    _buildSheetHTML(item, source, originPkgId) {
      const titleEl = item.querySelector(source === 'pkg' ? '.cz-pkg-card__item-title' : '.cz-pkg-loose-item__title');
      const articleName = titleEl?.textContent?.trim() || 'Articolo';

      const title = source === 'loose' ? 'Dove lo metti?' : 'Sposta dove?';

      let actionsHTML = '';

      // Voci "Sposta in Pacco N" (solo pacchi diversi dall'origine).
      this._packages.forEach((pkg, i) => {
        if (pkg.id === originPkgId) return;
        const label = source === 'loose' ? 'Aggiungi a' : 'Sposta in';
        actionsHTML += `
          <li><button type="button" class="cz-pkg-sheet__act" data-sheet-action="move" data-target="${pkg.id}">
            <span class="cz-pkg-sheet__act-icon">${i + 1}</span>
            ${label} Pacco ${i + 1}
          </button></li>
        `;
      });

      // Voce "+ Crea nuovo pacco" (primary).
      actionsHTML += `
        <li><button type="button" class="cz-pkg-sheet__act cz-pkg-sheet__act--primary" data-sheet-action="new">
          <span class="cz-pkg-sheet__act-icon">+</span>
          Crea nuovo pacco regalo
        </button></li>
      `;

      // Voce "Togli dal pacco" (danger) — solo se item viene da pacco.
      if (source === 'pkg') {
        actionsHTML += `
          <li><button type="button" class="cz-pkg-sheet__act cz-pkg-sheet__act--danger" data-sheet-action="trash">
            <span class="cz-pkg-sheet__act-icon">×</span>
            Togli dal pacco
          </button></li>
        `;
      }

      return `
        <div class="cz-pkg-sheet-back"></div>
        <div class="cz-pkg-sheet">
          <div class="cz-pkg-sheet__handle"></div>
          <div class="cz-pkg-sheet__head">
            <p class="cz-pkg-sheet__eyebrow">${this.escapeHTML(articleName)}</p>
            <h3 class="cz-pkg-sheet__title">${title}</h3>
          </div>
          <ul class="cz-pkg-sheet__list">${actionsHTML}</ul>
          <button type="button" class="cz-pkg-sheet__cancel" data-sheet-cancel>Annulla</button>
        </div>
      `;
    }

    _closeActionSheet() {
      if (this._activeSheet) {
        this._activeSheet.remove();
        this._activeSheet = null;
        this._activeSheetItem = null;
      }
    }

    _handleSheetAction(action, target) {
      const ctx = this._activeSheetItem;
      this._closeActionSheet();
      if (!ctx) return;

      // Simula un drop come da Pointer Events.
      const fakeItem = this.body.querySelector(`[data-line-item-key="${ctx.lineKey}"]`);
      if (!fakeItem) return;

      if (action === 'move') {
        const targetEl = this.body.querySelector(`.cz-pkg-card[data-package-id="${target}"]`);
        if (targetEl) this._commitDrop(fakeItem, targetEl);
      } else if (action === 'new') {
        const newEl = this.body.querySelector('.cz-pkg-drop-new');
        if (newEl) this._commitDrop(fakeItem, newEl);
      } else if (action === 'trash') {
        const trashEl = this.body.querySelector('.cz-pkg-drop-trash');
        if (trashEl) this._commitDrop(fakeItem, trashEl);
      }
    }

    async handleRemove(lineKey) {
      const cart = await fetch('/cart.js').then(r => r.json());
      const item = cart.items.find(it => it.key === lineKey);
      const pkgId = item?.properties?._package_id;
      const giftHandle = this.dataset.giftHandle;

      // Se questo è l'ULTIMO articolo del pacco (escludendo il service line),
      // chiediamo conferma prima di procedere — un pacco vuoto verrà eliminato.
      if (pkgId) {
        const productCount = cart.items.filter((it) =>
          it.properties?._package_id === pkgId && it.handle !== giftHandle
        ).length;
        if (productCount <= 1) {
          // Recupera la dedica del pacco per personalizzare il modal
          const service = cart.items.find((it) =>
            it.handle === giftHandle && it.properties?._package_id === pkgId
          );
          const dedica = service?.properties?.['Dedica'] || 'Con affetto';
          const ok = await CzConfirm.show({
            eyebrow: 'Eliminare pacchetto',
            title: 'Stai rimuovendo l\'<em>ultimo articolo</em> del pacchetto.',
            body: `Confermando, il pacchetto regalo «${CzConfirm._escapeHTML(dedica)}» verrà eliminato dal carrello.`,
            confirmText: 'Sì, elimina',
            cancelText: 'Annulla',
          });
          if (!ok) return;
        }
      }

      // Merge properties esistenti per non perdere _engraving_key (o altre).
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: lineKey,
          quantity: 1,
          properties: { ...(item.properties || {}), _package_id: null },
        }),
      });

      if (pkgId) {
        // Conta i PRODOTTI residui nel pacco (esclude il service line stesso,
        // che ha lo stesso _package_id ma handle === giftHandle).
        const newCart = await fetch('/cart.js').then(r => r.json());
        const remainingProducts = newCart.items.filter((it) =>
          it.properties?._package_id === pkgId && it.handle !== giftHandle
        ).length;

        if (remainingProducts === 0) {
          // Trova il service line via _package_id (NOT _package_service_for —
          // nel modello attuale il service ha _package_id == pkgId)
          const service = newCart.items.find((it) =>
            it.handle === giftHandle && it.properties?._package_id === pkgId
          );
          if (service) {
            // Salva foto/dedica in history (riapri lo stesso pacco entro 7gg
            // ripropone i valori — esistente UX)
            try {
              const history = JSON.parse(localStorage.getItem('cz_package_history') || '[]');
              history.push({
                packageId: pkgId,
                photoUrl: service.properties?.['Foto polaroid'] || null,
                dedication: service.properties?.['Dedica'] || null,
                savedAt: Date.now(),
              });
              const cutoff = Date.now() - (7 * 24 * 60 * 60 * 1000);
              const pruned = history.filter((h) => h.savedAt > cutoff);
              localStorage.setItem('cz_package_history', JSON.stringify(pruned));
            } catch (_) { /* localStorage disabled */ }

            await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: service.key, quantity: 0 }),
            });
          }
        }
      }

      document.dispatchEvent(new CustomEvent('cz:cart:updated', { detail: { source: 'pkg-remove-from-pkg' } }));
    }

    async handleEdit(packageId) {
      await CzPackageOps.refresh();
      const snap = CzPackageOps.snapshot();
      const pkg = snap.packages.find((p) => p.id === packageId);
      if (!pkg) return;
      document.dispatchEvent(new CustomEvent('cz:gift-drawer:open', {
        detail: {
          source: 'cart-edit',
          packageId,
          serviceLineKey: pkg.serviceLineKey,
          currentConfig: {
            photoUrl: pkg.photoUrl,
            photoPublicId: pkg.photoPublicId,
            dedication: pkg.dedication,
          },
        },
      }));
    }

    async handleCreateEmpty() {
      const newPkgId = await this.createEmptyPackage();
      if (!newPkgId) return;
      this.show60sToast(newPkgId);
    }

    async createEmptyPackage() {
      const giftHandle = this.dataset.giftHandle;
      if (!giftHandle) {
        alert('Configurazione mancante: gift_package_product_handle non impostato.');
        return null;
      }
      const pkgId = `PKG-${Date.now().toString(36).slice(-4)}${Math.random().toString(36).slice(2, 6)}`;

      const productRes = await fetch(`/products/${giftHandle}.js`);
      const product = await productRes.json();
      const variantId = product.variants[0].id;

      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1,
            properties: { _package_service_for: pkgId },
          }],
        }),
      });

      document.dispatchEvent(new CustomEvent('cz:cart:updated', { detail: { source: 'pkg-create' } }));
      return pkgId;
    }

    show60sToast(pkgId) {
      const toast = document.createElement('div');
      toast.className = 'cz-pkg-toast';
      toast.innerHTML = `
        <span>Nuovo pacco vuoto. Assegna almeno 1 articolo entro <strong data-countdown>60</strong>s.</span>
        <button type="button" data-cancel-create>Annulla</button>
      `;
      toast.style.cssText = 'position:fixed;bottom:20px;left:50%;transform:translateX(-50%);background:var(--sys-ink);color:#fff;padding:12px 16px;border-radius:4px;font-family:Inter,sans-serif;font-size:12px;z-index:300;display:flex;gap:12px;align-items:center;';
      toast.querySelector('button').style.cssText = 'background:transparent;color:#b89556;border:0;text-decoration:underline;cursor:pointer;font-size:11px;';
      document.body.appendChild(toast);

      let remaining = 60;
      const interval = setInterval(() => {
        remaining--;
        const countdownEl = toast.querySelector('[data-countdown]');
        if (countdownEl) countdownEl.textContent = remaining;
        if (remaining <= 0) {
          clearInterval(interval);
          this.autoCancelEmpty(pkgId);
          toast.remove();
        }
      }, 1000);

      toast.querySelector('[data-cancel-create]').addEventListener('click', () => {
        clearInterval(interval);
        this.autoCancelEmpty(pkgId);
        toast.remove();
      });

      const onUpdate = async () => {
        const cart = await fetch('/cart.js').then(r => r.json());
        const hasItems = cart.items.some(it => it.properties?._package_id === pkgId);
        if (hasItems) {
          clearInterval(interval);
          toast.remove();
          document.removeEventListener('cz:cart:updated', onUpdate);
        }
      };
      document.addEventListener('cz:cart:updated', onUpdate);
    }

    async autoCancelEmpty(pkgId) {
      const cart = await fetch('/cart.js').then(r => r.json());
      const serviceItem = cart.items.find(it => it.properties?._package_service_for === pkgId);
      if (serviceItem) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: serviceItem.key, quantity: 0 }),
        });
        document.dispatchEvent(new CustomEvent('cz:cart:updated', { detail: { source: 'pkg-auto-cancel' } }));
      }
    }

    escapeHTML(str) {
      return String(str || '').replace(/[&<>"']/g, m => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
      }[m]));
    }
  }

  if (!customElements.get('cz-cart-package-drawer')) {
    customElements.define('cz-cart-package-drawer', CzCartPackageDrawer);
  }

})();
